import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const logoUrl = "https://customer-assets.emergentagent.com/job_boxmanager-1/artifacts/zhtws8oa_Copia%20de%20Blanco%20y%20negro%20minimalista%20monograma%20tipografico%20logotipo%20.png";
const homeLogoUrl = "https://customer-assets.emergentagent.com/job_boxmanager-1/artifacts/6kjwqob0_Copia%20de%20Blanco%20y%20negro%20minimalista%20monograma%20tipografico%20logotipo%20%20%282880%20x%202304%20px%29%20%283000%20x%201900%20px%29.png";
const poweredByLogoUrl = "https://customer-assets.emergentagent.com/job_boxmanager-1/artifacts/b9r814vb_thumbnail_HIIT-AF.png";
axios.defaults.baseURL = API;

function useAuth() {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("hiit_user");
    return raw ? JSON.parse(raw) : null;
  });
  const token = user?.access_token;
  useEffect(() => {
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    else delete axios.defaults.headers.common["Authorization"];
  }, [token]);
  return { user, setUser: (u)=>{ setUser(u); if(u) localStorage.setItem("hiit_user", JSON.stringify(u)); else localStorage.removeItem("hiit_user"); } };
}

const Header = ({ user, onLogout }) => (
  <div className="w-full bg-black text-white border-b border-zinc-800">
    <div className="max-w-6xl mx-auto flex items-center gap-6 py-3 px-4">
      <img src={logoUrl} alt="Rank Box" className="h-10 w-10 rounded-full" />
      <Link to="/" className="font-semibold tracking-wide" data-testid="nav-home">Rank Box EC</Link>
      <div className="ml-auto flex items-center gap-3">
        <Link to="/rankings" className="text-yellow-400" data-testid="nav-rankings">Rankings</Link>
        <Link to="/competitions" className="text-yellow-400" data-testid="nav-competitions">Competencias</Link>
        <Link to="/search" className="text-yellow-400" data-testid="nav-search">Buscar peleador</Link>
        {user ? (
          <>
            {user.user.role === "admin" && (<Link to="/admin" data-testid="nav-admin" className="text-yellow-400">Admin</Link>)}
            {user.user.role === "gym_admin" && (
              <Link to="/affiliate" data-testid="nav-affiliate" className="text-yellow-400">Gimnasio</Link>
            )}
            <Button data-testid="logout-button" onClick={onLogout} className="bg-yellow-400 text-black hover:bg-yellow-300">Salir</Button>
          </>
        ) : (<Link to="/login" data-testid="nav-login" className="text-yellow-400">Ingresar</Link>)}
      </div>
    </div>
  </div>
);

const Login = ({ onLogged }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e?.preventDefault();
    try {
      const form = new URLSearchParams();
      form.append("username", email);
      form.append("password", password);
      const { data } = await axios.post(`/auth/login`, form);
      onLogged(data);
      toast.success("Bienvenido");
      const role = data.user.role;
      if (role === 'admin') window.location.href = '/admin';
      else if (role === 'gym_admin') window.location.href = '/affiliate';
      else window.location.href = '/';
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error de login");
    }
  };
  return (
    <div className="min-h-[70vh] grid place-items-center bg-zinc-50">
      <Card className="w-full max-w-md border-zinc-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt="Rank Box" className="h-10 w-10" />
            <CardTitle className="text-xl">Ingreso Rank Box EC</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3" autoComplete="off">
            <Input 
              data-testid="login-email" 
              placeholder="Email" 
              type="email"
              value={email} 
              onChange={(e)=>setEmail(e.target.value)}
              autoComplete="off"
              name="email-login"
            />
            <Input 
              data-testid="login-password" 
              placeholder="Contraseña" 
              type="password" 
              value={password} 
              onChange={(e)=>setPassword(e.target.value)}
              autoComplete="new-password"
              name="password-login"
            />
            <Button data-testid="login-submit" type="submit" className="w-full bg-yellow-400 text-black hover:bg-yellow-300">Ingresar</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const Rankings = () => {
  const [items, setItems] = useState([]);
  const [edadOpts, setEdadOpts] = useState([]);
  const [pesoOpts, setPesoOpts] = useState([]);
  const [selectedGenero, setSelectedGenero] = useState("");
  const [selectedEdad, setSelectedEdad] = useState("");
  const [selectedPeso, setSelectedPeso] = useState("");
  const [fightersMap, setFightersMap] = useState({});

  useEffect(() => { (async () => {
      const { data } = await axios.get(`/rankings/absolute`);
      setItems(data);
      const { data: edades } = await axios.get(`/config/categorias-edad`);
      setEdadOpts(edades.map(e=>e.nombre_categoria));
      const { data: pesos } = await axios.get(`/config/categorias-peso`, { params: { federacion: 'IBA' }});
      // Eliminar duplicados de categorías de peso
      const uniquePesos = [...new Set(pesos.map(p=>p.nombre_categoria))];
      setPesoOpts(['Absoluta', ...uniquePesos]);
      // Cargar peleadores para mapear IDs a nombres
      const { data: fighters } = await axios.get(`/fighters`);
      const map = {};
      fighters.forEach(f => { map[f.id] = f; });
      setFightersMap(map);
    })(); }, []);
  const filtrar = async () => {
    // Construir params solo con los valores seleccionados
    const params = {};
    if (selectedGenero) params.genero = selectedGenero;
    if (selectedEdad) params.categoria_edad = selectedEdad;
    if (selectedPeso) params.categoria_peso = selectedPeso;
    
    // Si no hay ningún filtro, mostrar ranking absoluto
    if (Object.keys(params).length === 0) {
      const { data } = await axios.get(`/rankings/absolute`);
      setItems(data || []);
      return;
    }
    
    const { data } = await axios.get(`/rankings/by-category`, { params });
    setItems(data || []);
  };
  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-2xl font-semibold mb-4">Ranking</h1>
      <div className="flex gap-2 mb-3 flex-wrap">
        <select data-testid="filter-genero" className="border rounded px-2 py-1 min-w-[200px]" value={selectedGenero} onChange={(e)=>setSelectedGenero(e.target.value)}>
          <option value="">Todos los géneros</option>
          <option value="Masculino">Masculino</option>
          <option value="Femenino">Femenino</option>
        </select>
        <select data-testid="filter-edad" className="border rounded px-2 py-1 min-w-[200px]" value={selectedEdad} onChange={(e)=>setSelectedEdad(e.target.value)}>
          <option value="">Todas las edades</option>
          {edadOpts.map(opt=> <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select data-testid="filter-peso" className="border rounded px-2 py-1 min-w-[200px]" value={selectedPeso} onChange={(e)=>setSelectedPeso(e.target.value)}>
          <option value="">Todas las categorías de peso</option>
          {pesoOpts.map(opt=> <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <Button data-testid="filter-apply" onClick={filtrar} className="bg-black text-white">Aplicar</Button>
      </div>
      <div className="rounded-lg border border-zinc-200 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-zinc-100"><tr><th className="p-2">Ranking</th><th className="p-2">Peleador</th><th className="p-2">Victorias</th><th className="p-2">Empates</th><th className="p-2">Derrotas</th></tr></thead>
          <tbody>
            {items.map((r, i)=> {
              const fighter = fightersMap[r.peleador_id];
              const position = i + 1;
              const medalEmoji = position === 1 ? '🥇' : position === 2 ? '🥈' : position === 3 ? '🥉' : '';
              const medalBg = position === 1 ? 'bg-yellow-50' : position === 2 ? 'bg-gray-50' : position === 3 ? 'bg-orange-50' : '';
              
              return (
                <tr key={i} className={`${medalBg || (i % 2 === 0 ? 'bg-white' : 'bg-zinc-50')}`}>
                  <td className="p-2 font-semibold" data-testid={`ranking-position-${position}`}>
                    <span className="flex items-center gap-2">
                      {medalEmoji && <span className="text-2xl">{medalEmoji}</span>}
                      <span>{position}</span>
                    </span>
                  </td>
                  <td className="p-2 font-medium" data-testid={`ranking-row-${i}`}>{fighter ? fighter.nombre_completo : r.peleador_id}</td>
                  <td className="p-2 text-green-700 font-semibold">{r.victorias_totales}</td>
                  <td className="p-2 text-gray-600">{r.empates_totales}</td>
                  <td className="p-2 text-red-600">{r.derrotas_totales}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Competitions = () => {
  const [items, setItems] = useState([]);
  const [gymsMap, setGymsMap] = useState({});

  useEffect(()=>{ (async()=>{
    const { data } = await axios.get(`/events`);
    const sorted = [...(data || [])].sort((a, b) => {
      if (!a.fecha_competencia && !b.fecha_competencia) return 0;
      if (!a.fecha_competencia) return 1;
      if (!b.fecha_competencia) return -1;
      const da = new Date(a.fecha_competencia);
      const db = new Date(b.fecha_competencia);
      return da - db; // más cercana primero
    });
    setItems(sorted);
    try {
      const gymsRes = await axios.get(`/gyms`);
      const map = {};
      gymsRes.data.forEach(g => { map[g.id] = g; });
      setGymsMap(map);
    } catch(e) {
      // si falla, simplemente no mostramos logos
    }
  })(); },[]);

  const formatFecha = (iso) => {
    if (!iso) return '—';
    // Espera formato YYYY-MM-DD o YYYY-MM-DDTHH:MM:SS
    const [datePart] = String(iso).split('T');
    const [y, m, d] = datePart.split('-');
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between border-b border-zinc-200 pb-3 mb-1">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-900">Eventos</h1>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((c)=> {
          const gym = gymsMap[c.gimnasio_organizador_id];
          return (
            <Card
              key={c.id}
              className="border border-yellow-300 bg-yellow-400/80 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`event-${c.id}`}
            >
              <Link to={`/events/${c.id}`} className="flex flex-col gap-2 p-3 h-full">
                <div className="flex items-start gap-3">
                  {gym?.logo_url && (
                    <img
                      src={gym.logo_url}
                      alt={gym.nombre_gimnasio || 'Logo gimnasio'}
                      className="h-12 w-12 rounded-full object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 space-y-1">
                    <div className="text-xs uppercase tracking-wide text-zinc-500">Evento</div>
                    <div className="text-sm sm:text-base font-semibold text-zinc-900">{c.nombre_competencia}</div>
                    <div className="text-xs sm:text-sm text-zinc-600">
                      <span className="font-semibold">Fecha:</span> {formatFecha(c.fecha_competencia)}
                    </div>
                    <div className="text-xs sm:text-sm text-zinc-600">
                      <span className="font-semibold">Lugar:</span> {c.pais||'—'}, {c.ciudad||'—'}
                    </div>
                    <div className="text-xs sm:text-sm text-zinc-600">
                      <span className="font-semibold">Dirección:</span> {c.direccion||'—'}
                    </div>
                    <div className="text-xs sm:text-sm text-zinc-600">
                      <span className="font-semibold">Modalidad de pelea:</span> {c.modalidad||'—'}
                    </div>
                    <div className="text-xs sm:text-sm text-zinc-600">
                      <span className="font-semibold">Gimnasio:</span> {gym?.nombre_gimnasio || c.gimnasio_organizador_id}
                    </div>
                    {c.ubicacion && (
                      <div className="text-xs sm:text-sm text-zinc-600 mt-1">
                        <span className="font-semibold">Ubicación:</span>{' '}
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.ubicacion)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline inline-flex items-center gap-1"
                        >
                          <span>📍</span>
                          <span>{c.ubicacion}</span>
                        </a>
                      </div>
                    )}

                  </div>
                </div>
              </Link>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const CompetitionDetail = () => {
  const { id } = useParams();
  const auth = useAuth();
  const [ev, setEv] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [q, setQ] = useState("");
  const [pesoEvento, setPesoEvento] = useState("");
  const [found, setFound] = useState(null);
  const [parts, setParts] = useState([]);
  const [matches, setMatches] = useState([]);
  const [fightersMap, setFightersMap] = useState({});
  const [results, setResults] = useState({});
  const [podio, setPodio] = useState([]);
  const [podioLoaded, setPodioLoaded] = useState(false);
  
  // Registration link states
  const [registrationLink, setRegistrationLink] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [loadingLink, setLoadingLink] = useState(false);
  
  // Registrations management states
  const [registrations, setRegistrations] = useState([]);
  
  // Fighter detail modal
  const [selectedFighter, setSelectedFighter] = useState(null);
  const [showFighterModal, setShowFighterModal] = useState(false);
  
  // Manual match creation
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [creatingMatch, setCreatingMatch] = useState(false);
  const [registrationsLoaded, setRegistrationsLoaded] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const pesoColors = {
    'Peso mínimo': 'bg-emerald-100 border-emerald-300',
    'Peso minimosca': 'bg-emerald-50 border-emerald-200',
    'Peso mosca': 'bg-sky-100 border-sky-300',
    'Peso gallo': 'bg-sky-50 border-sky-200',
    'Peso pluma': 'bg-indigo-100 border-indigo-300',
    'Peso ligero': 'bg-indigo-50 border-indigo-200',
    'Peso superligero': 'bg-purple-100 border-purple-300',
    'Peso welter': 'bg-purple-50 border-purple-200',
    'Peso superwelter': 'bg-pink-100 border-pink-300',
    'Peso medio': 'bg-pink-50 border-pink-200',
    'Peso semipesado': 'bg-amber-100 border-amber-300',
    'Peso crucero': 'bg-amber-50 border-amber-200',
    'Peso pesado': 'bg-orange-100 border-orange-300',
    'Peso superpesado': 'bg-orange-50 border-orange-200',
  };

  useEffect(()=>{
    if (ev) {
      setEditing({
        nombre_competencia: ev.nombre_competencia || '',
        fecha_competencia: ev.fecha_competencia || '',
        gimnasio_organizador_id: ev.gimnasio_organizador_id,
        pais: ev.pais || '',
        ciudad: ev.ciudad || '',
        direccion: ev.direccion || '',
        modalidad: ev.modalidad || 'Eliminatorias',
        ubicacion: ev.ubicacion || '',
        estado: ev.estado || 'Programada',
        tipo_evento: ev.tipo_evento || null,
      });
    }
  }, [ev]);

  const load = async()=>{
    const { data } = await axios.get(`/events/${id}`); setEv(data);
    const p = await axios.get(`/events/${id}/participants`); setParts(p.data||[]);
    const m = await axios.get(`/events/${id}/matches`); setMatches(m.data||[]);
    const f = await axios.get(`/fighters`);
    const map = {};
    f.data.forEach(ft => { map[ft.id] = ft; });
    setFightersMap(map);
    const resState = {};
    (m.data || []).forEach(mt => {
      resState[mt.id] = { ganador_id: mt.ganador_id || "", tipo_resultado: mt.tipo_resultado || "" };
    });
    setResults(resState);
  };

  useEffect(()=>{ load(); },[id]);

  // Load registration link
  const loadRegistrationLink = async () => {
    try {
      const { data } = await axios.get(`/events/${id}/registration-link`);
      setRegistrationLink(data);
    } catch (e) {
      if (e.response?.status !== 404) {
        console.error('Error loading registration link:', e);
      }
    }
  };
  
  const generateRegistrationLink = async () => {
    setLoadingLink(true);
    try {
      const { data } = await axios.post(`/events/${id}/registration-link`, {
        auto_approve: false
      });
      setRegistrationLink(data);
      setShowLinkModal(true);
      toast.success('Link de inscripción generado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar link');
    } finally {
      setLoadingLink(false);
    }
  };
  
  const copyLinkToClipboard = () => {
    if (registrationLink) {
      navigator.clipboard.writeText(registrationLink.public_url);
      toast.success('Link copiado al portapapeles');
    }
  };
  
  const toggleLinkActive = async () => {
    try {
      await axios.patch(`/events/${id}/registration-link`, {
        is_active: !registrationLink.is_active
      });
      await loadRegistrationLink();
      toast.success(registrationLink.is_active ? 'Link desactivado' : 'Link activado');
    } catch (e) {
      toast.error('Error al actualizar link');
    }
  };
  
  // Load registrations
  const loadRegistrations = async () => {
    try {
      console.log('Loading registrations for event:', id);
      const { data } = await axios.get(`/events/${id}/registrations`);
      console.log('Registrations loaded:', data.length);
      setRegistrations(data);
      setRegistrationsLoaded(true);
    } catch (e) {
      console.error('Error loading registrations:', e);
      if (e.response?.status !== 404) {
        toast.error('Error al cargar inscripciones');
      }
    }
  };
  
  const approveRegistration = async (registrationId) => {
    try {
      await axios.patch(`/registrations/${registrationId}/status`, null, {
        params: { status: 'approved' }
      });
      toast.success('Inscripción aprobada');
      await loadRegistrations();
      await loadRegistrationLink();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al aprobar');
    }
  };
  
  const rejectRegistration = async (registrationId) => {
    if (!window.confirm('¿Rechazar esta inscripción?')) return;
    try {
      await axios.patch(`/registrations/${registrationId}/status`, null, {
        params: { status: 'rejected' }
      });
      toast.success('Inscripción rechazada');
      await loadRegistrations();
      await loadRegistrationLink();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al rechazar');
    }
  };
  
  const withdrawRegistration = async (registrationId) => {
    if (!window.confirm('¿Retirar esta inscripción? El participante será removido del evento.')) return;
    try {
      await axios.patch(`/registrations/${registrationId}/status`, null, {
        params: { status: 'withdrawn' }
      });
      toast.success('Inscripción retirada');
      await loadRegistrations();
      await load(); // Recargar participantes y matches
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al retirar');
    }
  };
  
  const viewProfile = (registration) => {
    setSelectedRegistration(registration);
    setShowProfileModal(true);
  };
  
  useEffect(() => {
    if (id) {
      loadRegistrationLink();
      setTimeout(() => {
        loadRegistrations();
      }, 1000);
    }
  }, [id]);


  useEffect(()=>{ (async()=>{
    try {
      const { data } = await axios.get(`/events/${id}/podio`);
      setPodio(data || []);
    } catch(e) {
      // silencioso
    } finally {
      setPodioLoaded(true);
    }
  })(); }, [id]);

  const role = auth.user?.user?.role;
  const canDeleteEvent = role === 'admin' || (role === 'gym_admin' && auth.user?.user?.gimnasio_id === ev?.gimnasio_organizador_id);

  const search = async()=>{
    const value = q.trim();
    if (!value) { toast.error('Ingresa el número de identificación del peleador'); return; }
    // Buscar localmente en el mapa de peleadores cargado
    const allFighters = Object.values(fightersMap || {});
    let fx = allFighters.find(f=> String(f.numero_identificacion || f.numeroIdentificacion || '').trim() === value);
    if (!fx && /^\d+$/.test(value)){
      // Intentar también sin ceros/espacios
      fx = allFighters.find(f=> String(f.numero_identificacion || f.numeroIdentificacion || '') === String(parseInt(value,10)));
    }
    if (!fx){
      setFound(null);
      toast.error('No encontrado');
      return;
    }
    setFound({
      peleador: fx,
      categoria_edad: fx.categoria_edad,
      categoria_peso: fx.categoria_peso,
      gimnasio: null,
      record: { victorias: 0, derrotas: 0, empates: 0 },
    });
  };
  const enroll = async()=>{
    const value = q.trim();
    if (!value){ toast.error('Ingresa el número de identificación del peleador'); return; }
    const peso = pesoEvento ? parseFloat(pesoEvento) : undefined;
    try{
      await axios.post(`/events/${id}/participants`, { numero_identificacion:value, peso_evento: peso });
      toast.success('Inscrito y emparejamientos generados');
      setPesoEvento("");
      await load();
    } catch(e){ toast.error(e?.response?.data?.detail || 'Error al inscribir'); }
  };

  if (!ev) return null;

  // Preparar estructura de peleas agrupadas por categoría y ronda para render
  const groups = {};
  for (const m of matches) {
    const gen = m.genero || 'Sin género';
    const key = `${gen}||${m.categoria_edad}||${m.categoria_peso}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(m);
  }
  const roundOrder = ['Octavos', 'Cuartos', 'Semifinal', 'Final', 'Pelea suelta', null, undefined];

  const renderMatchesByGroup = () => {
    if (!matches.length) return <div className="text-xs text-zinc-500">Aún no hay peleas generadas.</div>;

    return Object.entries(groups).map(([key, groupMatches]) => {
      const [gen, catEdad, catPeso] = key.split('||');

      // Agrupar por ronda dentro del grupo
      const byRound = {};
      for (const m of groupMatches) {
        const r = m.ronda || 'Pelea suelta';
        if (!byRound[r]) byRound[r] = [];
        byRound[r].push(m);
      }

      const sortedRounds = Object.keys(byRound).sort((a,b)=>{
        const ia = roundOrder.indexOf(a);
        const ib = roundOrder.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });

      return (
        <div key={key} className="border rounded p-3 space-y-3">
          <div className="font-semibold">
            {gen} • {catEdad} / {catPeso}
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {sortedRounds.map(ronda => (
              <div key={ronda} className="space-y-2">
                <div className="text-xs font-semibold uppercase text-zinc-600">
                  {ronda === 'Pelea suelta' ? 'Peleas' : ronda}
                </div>
                <div className="space-y-2">
                  {byRound[ronda]
                    .sort((a,b)=> (a.orden_en_ronda||0) - (b.orden_en_ronda||0))
                    .map(m => {
                      const rojo = fightersMap[m.peleador_rojo_id];
                      const azul = fightersMap[m.peleador_azul_id];
                      const res = results[m.id] || { ganador_id: m.ganador_id || "", tipo_resultado: m.tipo_resultado || "" };
                      return (
                        <div key={m.id} className="space-y-1 border rounded px-2 py-1">
                          <div className="flex items-center justify-between text-[11px] text-zinc-600">
                            <span>{ronda}</span>
                            {m.orden_en_ronda && <span>Pelea {m.orden_en_ronda}</span>}
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            <div>
                              <span className="text-red-600 font-semibold text-sm mr-1">Esquina Roja</span>
                              {rojo ? (
                                <Link to={`/fighters/${rojo.id}/edit`} className="underline text-zinc-700 hover:text-zinc-900">
                                  {rojo.nombre_completo}
                                </Link>
                              ) : m.peleador_rojo_id}
                              {rojo && <span className="text-xs text-zinc-500 ml-1">({rojo.numero_total_peleas || 0} peleas)</span>}
                            </div>
                            <span className="text-xs text-zinc-500">vs</span>
                            <div>
                              <span className="text-blue-600 font-semibold text-sm mr-1">Esquina Azul</span>
                              {azul ? (
                                <Link to={`/fighters/${azul.id}/edit`} className="underline text-zinc-700 hover:text-zinc-900">
                                  {azul.nombre_completo}
                                </Link>
                              ) : m.peleador_azul_id}
                              {azul && <span className="text-xs text-zinc-500 ml-1">({azul.numero_total_peleas || 0} peleas)</span>}
                            </div>
                            {m.experience_warning && (
                              <div className="flex items-center gap-1 text-orange-600 text-xs">
                                <span className="text-lg">👁️</span>
                                <span>Diferencia de experiencia</span>
                              </div>
                            )}
                            {m.same_gym_warning && (
                              <div className="text-xs text-yellow-600">
                                ⚠️ Mismo gimnasio
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="font-medium">Resultado:</span>
                            {/* Primero tipo de resultado */}
                            <select
                              value={res.tipo_resultado}
                              onChange={(e)=>{
                                const v = e.target.value;
                                setResults(prev=>({
                                  ...prev,
                                  [m.id]: { ...(prev[m.id]||{}), tipo_resultado:v, ganador_id: v === 'Empate' ? '' : (prev[m.id]?.ganador_id || '') }
                                }));
                              }}
                              className="border rounded px-1 py-0.5 text-xs"
                            >
                              <option value="">Tipo de resultado</option>
                              <option value="Empate">Empate</option>
                              <option value="Decisión dividida">Decisión dividida</option>
                              <option value="Decisión unánime">Decisión unánime</option>
                              <option value="KO">KO</option>
                              <option value="KO técnico">KO técnico</option>
                              <option value="Retirada">Retirada</option>
                              <option value="No show">No show</option>
                            </select>

                            {/* Luego ganador, deshabilitado si es empate */}
                            <select
                              value={res.ganador_id}
                              disabled={res.tipo_resultado === 'Empate'}
                              onChange={(e)=>{
                                const v = e.target.value;
                                setResults(prev=>({...prev, [m.id]: { ...(prev[m.id]||{}), ganador_id:v }}));
                              }}
                              className="border rounded px-1 py-0.5 text-xs disabled:bg-zinc-100"
                            >
                              <option value="">Seleccionar ganador</option>
                              <option value={m.peleador_rojo_id}>{rojo?.nombre_completo || 'Rojo'}</option>
                              <option value={m.peleador_azul_id}>{azul?.nombre_completo || 'Azul'}</option>
                            </select>
                            <button
                              className="px-2 py-0.5 bg-yellow-400 text-black rounded"
                              onClick={async()=>{
                                const r = results[m.id] || {};
                                if (!r.tipo_resultado || (r.tipo_resultado !== 'Empate' && !r.ganador_id)) {
                                  toast.error('Selecciona tipo de resultado y, si no es empate, un ganador');
                                  return;
                                }
                                try {
                                  await axios.post(`/matches/${m.id}/result`, r);
                                  toast.success('Resultado guardado');
                                  await load();
                                } catch(e) {
                                  toast.error(e?.response?.data?.detail || 'Error al guardar resultado');
                                }
                              }}
                            >
                              Guardar
                            </button>
                            <button
                              className="px-2 py-0.5 bg-red-500 text-white rounded text-xs"
                              onClick={async()=>{
                                if (!window.confirm('¿Eliminar esta pelea?')) return;
                                try {
                                  await axios.delete(`/matches/${m.id}`);
                                  toast.success('Pelea eliminada');
                                  await load();
                                } catch(e) {
                                  toast.error(e?.response?.data?.detail || 'No se pudo eliminar la pelea');
                                }
                              }}
                            >
                              Eliminar pelea
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-8">
      <div className="flex items-start justify-between gap-6 border-b border-zinc-200 pb-5">
        <div className="space-y-3">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-900">{ev.nombre_competencia}</h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs md:text-sm text-zinc-700">
            <div className="space-y-0.5">
              <div className="font-semibold text-zinc-900">Fecha</div>
              <div>{ev.fecha_competencia || '—'}</div>
            </div>
            <div className="space-y-0.5">
              <div className="font-semibold text-zinc-900">País</div>
              <div>{ev.pais || '—'}</div>
            </div>
            <div className="space-y-0.5">
              <div className="font-semibold text-zinc-900">Ciudad</div>
              <div>{ev.ciudad || '—'}</div>
            </div>
            <div className="space-y-0.5">
              <div className="font-semibold text-zinc-900">Dirección</div>
              <div>{ev.direccion || '—'}</div>
            </div>
            <div className="space-y-0.5">
              <div className="font-semibold text-zinc-900">Modalidad de pelea</div>
              <div>{ev.modalidad || '—'}</div>
            </div>
            <div className="space-y-0.5">
              <div className="font-semibold text-zinc-900">Estado del evento</div>
              <div className={ev.estado === 'Programada' ? 'text-emerald-700 font-semibold' : 'text-zinc-700'}>{ev.estado || '—'}</div>
            </div>
            {ev.ubicacion && (
              <div className="space-y-0.5 sm:col-span-2 lg:col-span-3">
                <div className="font-semibold text-zinc-900">Ubicación (Google Maps)</div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.ubicacion)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-zinc-800 underline"
                >
                  <span>📍</span>
                  <span>{ev.ubicacion}</span>
                </a>
              </div>
            )}
          </div>
        </div>
        {canDeleteEvent && (
          <Button
            variant="destructive"
            className="bg-red-600 text-white text-xs px-3 py-1"
            onClick={async()=>{
              if (!window.confirm('¿Eliminar este evento y toda su información (participantes y peleas) siempre que no tenga resultados?')) return;
              try {
                await axios.delete(`/events/${id}`);
                toast.success('Evento eliminado');
                window.location.href = '/competitions';
              } catch(e) {
                toast.error(e?.response?.data?.detail || 'No se pudo eliminar el evento');
              }
            }}
          >
            Eliminar evento
          </Button>
        )}
      </div>

      {showEdit && editing && (
        <Card className="border border-zinc-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Editar evento</CardTitle>
            <CardDescription className="text-xs md:text-sm text-zinc-500">Modifica la información básica de este evento.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Nombre del evento</label>
                <Input
                  value={editing.nombre_competencia}
                  onChange={(e)=>setEditing({...editing, nombre_competencia:e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Fecha (AAAA-MM-DD)</label>
                <Input
                  type="date"
                  value={(editing.fecha_competencia||'').split('T')[0]}
                  onChange={(e)=>setEditing({...editing, fecha_competencia:e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">País</label>
                <Input
                  value={editing.pais}
                  onChange={(e)=>setEditing({...editing, pais:e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Ciudad</label>
                <Input
                  value={editing.ciudad}
                  onChange={(e)=>setEditing({...editing, ciudad:e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Dirección</label>
                <Input
                  value={editing.direccion}
                  onChange={(e)=>setEditing({...editing, direccion:e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Modalidad de pelea</label>
                <select
                  className="border rounded px-2 py-1 w-full"
                  value={editing.modalidad}
                  onChange={(e)=>setEditing({...editing, modalidad:e.target.value})}
                >
                  <option>Eliminatorias</option>
                  <option>Pelea única</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Ubicación (texto para Google Maps)</label>
                <Input
                  value={editing.ubicacion}
                  onChange={(e)=>setEditing({...editing, ubicacion:e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Estado del evento</label>
                <select
                  className="border rounded px-2 py-1 w-full"
                  value={editing.estado}
                  onChange={(e)=>setEditing({...editing, estado:e.target.value})}
                >
                  <option>Programada</option>
                  <option>En curso</option>
                  <option>Finalizada</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs px-3 py-1 border-zinc-300 text-zinc-800 hover:bg-zinc-100"
                onClick={()=> setShowEdit(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                className="text-xs px-3 py-1 bg-yellow-400 text-black hover:bg-yellow-300"
                onClick={async()=>{
                  try {
                    await axios.put(`/events/${id}`, editing);
                    toast.success('Evento actualizado');
                    await load();
                    setShowEdit(false);
                  } catch(e) {
                    toast.error(e?.response?.data?.detail || 'No se pudo actualizar el evento');
                  }
                }}
              >
                Guardar cambios
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {canDeleteEvent && !showEdit && (
        <div className="mb-4 flex gap-2 flex-wrap">
          <Button
            type="button"
            className="bg-black text-white text-sm px-4 py-2 hover:bg-zinc-800"
            onClick={()=> setShowEdit(true)}
          >
            Editar evento
          </Button>
          
          {registrationLink ? (
            <Button
              type="button"
              className="bg-yellow-500 text-black text-sm px-4 py-2 hover:bg-yellow-600"
              onClick={() => setShowLinkModal(true)}
            >
              📋 Ver Link de Inscripción
            </Button>
          ) : (
            <Button
              type="button"
              className="bg-green-600 text-white text-sm px-4 py-2 hover:bg-green-700"
              onClick={generateRegistrationLink}
              disabled={loadingLink}
            >
              {loadingLink ? '...' : '🔗 Generar Link de Inscripción'}
            </Button>
          )}
        </div>
      )}
      
      {showLinkModal && registrationLink && (
        <Card className="mb-4 border-2 border-yellow-400">
          <CardHeader>
            <CardTitle className="text-lg">🔗 Link Público de Inscripción</CardTitle>
            <CardDescription>Comparte este link para que los boxeadores se inscriban</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="bg-zinc-100 p-3 rounded border">
              <a 
                href={registrationLink.public_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline break-all text-sm font-medium"
              >
                {registrationLink.public_url}
              </a>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Button 
                onClick={() => window.open(registrationLink.public_url, '_blank')}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                🌐 Abrir Formulario
              </Button>
              
              <Button onClick={copyLinkToClipboard} className="bg-black text-white">
                📋 Copiar Link
              </Button>
              
              <Button onClick={toggleLinkActive} className={registrationLink.is_active ? "bg-red-600 text-white" : "bg-green-600 text-white"}>
                {registrationLink.is_active ? '🔴 Desactivar' : '🟢 Activar'}
              </Button>
              
              <Button onClick={() => setShowLinkModal(false)} variant="outline">
                Cerrar
              </Button>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-300 rounded p-3 text-sm">
              <strong>💡 Tip:</strong> Puedes compartir este link por WhatsApp, redes sociales o email para que los boxeadores se inscriban directamente.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profile Detail Modal */}
      {showProfileModal && selectedRegistration && (
        <Card className="mb-6 border-2 border-blue-500">
          <CardHeader className="bg-blue-50">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-xl">👤 Perfil Completo del Inscrito</CardTitle>
                <CardDescription>
                  Información detallada de la inscripción
                </CardDescription>
              </div>
              <Button
                onClick={() => setShowProfileModal(false)}
                variant="outline"
                size="sm"
              >
                ✕ Cerrar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {(() => {
              const reg = selectedRegistration;
              const data = reg.registration_data || {};
              const statusColors = {
                pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                approved: 'bg-green-100 text-green-800 border-green-300',
                rejected: 'bg-red-100 text-red-800 border-red-300',
                withdrawn: 'bg-orange-100 text-orange-800 border-orange-300'
              };
              const statusText = {
                pending: 'Pendiente',
                approved: 'Aprobado',
                rejected: 'Rechazado',
                withdrawn: 'Retirado'
              };
              
              return (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Información Personal */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg border-b pb-2">📋 Información Personal</h3>
                    <div>
                      <label className="text-sm text-zinc-600">Nombre completo</label>
                      <p className="font-medium">{data.nombre_completo}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Cédula / Identificación</label>
                      <p className="font-medium">{data.numero_identificacion}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Fecha de nacimiento</label>
                      <p className="font-medium">{data.fecha_nacimiento}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Edad calculada</label>
                      <p className="font-medium">{reg.edad_calculada} años</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Género</label>
                      <p className="font-medium">{data.genero}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Teléfono</label>
                      <p className="font-medium">{data.telefono}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Email</label>
                      <p className="font-medium">{data.email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Ciudad / Provincia</label>
                      <p className="font-medium">{data.ciudad}{data.provincia ? ` / ${data.provincia}` : ''}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Nacionalidad</label>
                      <p className="font-medium">{data.nacionalidad || 'Ecuador'}</p>
                    </div>
                  </div>
                  
                  {/* Información Deportiva */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-lg border-b pb-2">🥊 Información Deportiva</h3>
                    <div>
                      <label className="text-sm text-zinc-600">Gimnasio</label>
                      <p className="font-medium">
                        {reg.gimnasio_nombre || (reg.pending_gym_data?.nombre_gimnasio) || '—'}
                        {reg.pending_gym_data && (
                          <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                            Nuevo - Requiere verificación
                          </span>
                        )}
                      </p>
                      {reg.pending_gym_data && (
                        <div className="mt-2 text-sm text-zinc-600 bg-yellow-50 p-2 rounded">
                          <p><strong>Ciudad:</strong> {reg.pending_gym_data.ciudad || '—'}</p>
                          <p><strong>Entrenador:</strong> {reg.pending_gym_data.nombre_entrenador || '—'}</p>
                          <p><strong>Teléfono:</strong> {reg.pending_gym_data.telefono_contacto || '—'}</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Entrenador</label>
                      <p className="font-medium">{data.nombre_entrenador || '—'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Peso</label>
                      <p className="font-medium text-lg">{data.peso_kg} kg</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Categoría de edad</label>
                      <p className="font-medium text-blue-700">{reg.categoria_edad || '—'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Categoría de peso</label>
                      <p className="font-medium text-blue-700">{reg.categoria_peso || '—'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Número de peleas previas</label>
                      <p className="font-medium">{data.numero_peleas_previas || 0} peleas</p>
                    </div>
                    
                    {/* Estado */}
                    <div>
                      <label className="text-sm text-zinc-600">Estado de inscripción</label>
                      <p className={`inline-block px-3 py-1 rounded border font-medium ${statusColors[reg.status]}`}>
                        {statusText[reg.status]}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-600">Fecha de inscripción</label>
                      <p className="font-medium">
                        {new Date(reg.created_at).toLocaleString('es-EC', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    
                    {/* Comprobante */}
                    {reg.comprobante_pago_url && (
                      <div>
                        <label className="text-sm text-zinc-600">Comprobante de pago</label>
                        <Button
                          size="sm"
                          onClick={() => {
                            const url = BACKEND_URL + reg.comprobante_pago_url;
                            window.open(url, '_blank');
                          }}
                          className="mt-1 w-full"
                        >
                          📄 Ver comprobante
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Registration Management Section */}
      {registrationLink && canDeleteEvent && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl">📋 Inscripciones Públicas</CardTitle>
            <CardDescription>
              Gestiona las inscripciones recibidas a través del link público
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Stats */}
            <div className="flex gap-4 mb-6">
              <div className="bg-zinc-100 px-4 py-2 rounded">
                <div className="text-2xl font-bold">{registrations.length}</div>
                <div className="text-sm text-zinc-600">Total</div>
              </div>
              <div className="bg-yellow-100 px-4 py-2 rounded">
                <div className="text-2xl font-bold text-yellow-700">
                  {registrations.filter(r => r.status === 'pending').length}
                </div>
                <div className="text-sm text-zinc-600">Pendientes</div>
              </div>
              <div className="bg-green-100 px-4 py-2 rounded">
                <div className="text-2xl font-bold text-green-700">
                  {registrations.filter(r => r.status === 'approved').length}
                </div>
                <div className="text-sm text-zinc-600">Aprobadas</div>
              </div>
              <div className="bg-red-100 px-4 py-2 rounded">
                <div className="text-2xl font-bold text-red-700">
                  {registrations.filter(r => r.status === 'rejected').length}
                </div>
                <div className="text-sm text-zinc-600">Rechazadas</div>
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex gap-3 mb-4 flex-wrap">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border rounded px-3 py-2 text-sm"
              >
                <option value="all">Todos los estados</option>
                <option value="pending">Pendientes</option>
                <option value="approved">Aprobados</option>
                <option value="rejected">Rechazados</option>
              </select>
              
              <Input
                placeholder="Buscar por nombre o cédula..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-xs"
              />
            </div>
            
            {/* Table */}
            {registrations.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No hay inscripciones todavía
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-100">
                    <tr>
                      <th className="p-2 text-left">Nombre</th>
                      <th className="p-2 text-left">Cédula</th>
                      <th className="p-2 text-left">Gimnasio</th>
                      <th className="p-2 text-left">Categoría</th>
                      <th className="p-2 text-left">Peso</th>
                      <th className="p-2 text-left">Estado</th>
                      <th className="p-2 text-left">Fecha</th>
                      <th className="p-2 text-left">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations
                      .filter(r => {
                        if (filterStatus !== 'all' && r.status !== filterStatus) return false;
                        if (searchTerm) {
                          const search = searchTerm.toLowerCase();
                          const name = r.registration_data?.nombre_completo?.toLowerCase() || '';
                          const cedula = r.registration_data?.numero_identificacion?.toLowerCase() || '';
                          return name.includes(search) || cedula.includes(search);
                        }
                        return true;
                      })
                      .map((reg) => {
                        const data = reg.registration_data || {};
                        const statusColors = {
                          pending: 'bg-yellow-100 text-yellow-800',
                          approved: 'bg-green-100 text-green-800',
                          rejected: 'bg-red-100 text-red-800'
                        };
                        const statusText = {
                          pending: 'Pendiente',
                          approved: 'Aprobado',
                          rejected: 'Rechazado'
                        };
                        
                        return (
                          <tr key={reg.id} className="border-b hover:bg-zinc-50">
                            <td className="p-2 font-medium">{data.nombre_completo}</td>
                            <td className="p-2 text-zinc-600">{data.numero_identificacion}</td>
                            <td className="p-2 text-zinc-600">
                              {reg.gimnasio_nombre || (reg.pending_gym_data?.nombre_gimnasio) || '—'}
                              {reg.pending_gym_data && (
                                <span className="ml-1 text-xs text-yellow-600">(Nuevo)</span>
                              )}
                            </td>
                            <td className="p-2 text-xs">
                              <div>{reg.categoria_edad || '—'}</div>
                              <div className="text-zinc-500">{reg.categoria_peso || '—'}</div>
                            </td>
                            <td className="p-2">{data.peso_kg} kg</td>
                            <td className="p-2">
                              <span className={`px-2 py-1 rounded text-xs ${statusColors[reg.status]}`}>
                                {statusText[reg.status]}
                              </span>
                            </td>
                            <td className="p-2 text-xs text-zinc-500">
                              {new Date(reg.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-2">
                              <div className="flex gap-1 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => viewProfile(reg)}
                                  className="text-xs px-2 py-1"
                                >
                                  👤 Ver perfil
                                </Button>
                                {reg.status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => approveRegistration(reg.id)}
                                      className="bg-green-600 text-white hover:bg-green-700 text-xs px-2 py-1"
                                    >
                                      ✓ Aprobar
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => rejectRegistration(reg.id)}
                                      className="bg-red-600 text-white hover:bg-red-700 text-xs px-2 py-1"
                                    >
                                      ✗ Rechazar
                                    </Button>
                                  </>
                                )}
                                {reg.status === 'approved' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => withdrawRegistration(reg.id)}
                                    className="text-xs px-2 py-1 text-orange-600 hover:bg-orange-50"
                                  >
                                    🚫 Retirar
                                  </Button>
                                )}
                                {reg.comprobante_pago_url && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const url = BACKEND_URL + reg.comprobante_pago_url;
                                      window.open(url, '_blank');
                                    }}
                                    className="text-xs px-2 py-1"
                                  >
                                    📄 Comprobante
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Inscribir peleador a este evento</CardTitle>
          <CardDescription className="text-xs md:text-sm text-zinc-500">Busca por número de identificación y define el peso para este evento.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              data-testid="enroll-id"
              placeholder="Número de identificación"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              className="max-w-xs"
            />
            <Button
              data-testid="enroll-search"
              onClick={search}
              className="bg-black text-white"
            >
              Buscar
            </Button>
          </div>
          {found && (
            <div className="border rounded p-3 text-sm space-y-2">
              <div className="font-medium">{found.peleador.nombre_completo} • {found.peleador.numero_identificacion}</div>
              <div className="text-zinc-600">{found.categoria_edad} / {found.categoria_peso} • Gimnasio: {found.gimnasio?.nombre_gimnasio||'—'}</div>
              <div>Record: {found.record.victorias}-{found.record.derrotas}-{found.record.empates}</div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  placeholder="Peso KG en este evento"
                  value={pesoEvento}
                  onChange={(e)=>setPesoEvento(e.target.value)}
                  className="w-64 max-w-xs"
                />
                <Button
                  data-testid="enroll-submit"
                  onClick={enroll}
                  className="bg-yellow-400 text-black"
                >
                  Inscribir y generar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="ml-auto text-xs px-3 py-1 border-zinc-300 text-zinc-800 hover:bg-zinc-100"
                  onClick={()=>{ window.location.href = `/fighters/${found.peleador.id}/edit`; }}
                >
                  Editar peleador
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Participantes</CardTitle>
              <CardDescription className="text-xs md:text-sm text-zinc-500">Listado de peleadores inscritos en este evento por categoría.</CardDescription>
            </div>
            {parts.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const userData = localStorage.getItem('hiit_user');
                    const token = userData ? JSON.parse(userData).access_token : null;
                    if (!token) {
                      toast.error('No estás autenticado');
                      return;
                    }
                    const response = await axios.get(`/events/${id}/participants/export-excel`, {
                      responseType: 'blob',
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `participantes_${ev.nombre_competencia.replace(/\s+/g, '_')}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('Excel descargado correctamente');
                  } catch (e) {
                    console.error('Error descargando Excel:', e);
                    toast.error(e?.response?.data?.detail || 'Error al descargar Excel');
                  }
                }}
                className="flex items-center gap-2"
              >
                <span>📥</span>
                <span className="hidden sm:inline">Descargar Excel</span>
                <span className="sm:hidden">Excel</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Botón para crear pelea manual */}
          {selectedParticipants.length === 2 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm">
                  <span className="font-semibold text-blue-900">2 participantes seleccionados</span>
                  <div className="text-xs text-blue-700 mt-1">
                    {selectedParticipants.map((pId, idx) => {
                      const p = parts.find(part => part.id === pId);
                      const f = p ? fightersMap[p.fighter_id] : null;
                      return (
                        <div key={pId}>
                          {idx + 1}. {f ? f.nombre_completo : pId} ({p?.categoria_edad} / {p?.categoria_peso})
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      const p1 = parts.find(p => p.id === selectedParticipants[0]);
                      const p2 = parts.find(p => p.id === selectedParticipants[1]);
                      
                      // Validar misma categoría
                      if (p1.categoria_edad !== p2.categoria_edad || 
                          p1.categoria_peso !== p2.categoria_peso ||
                          p1.genero !== p2.genero) {
                        toast.error('Los participantes deben ser de la misma categoría, peso y género');
                        return;
                      }
                      
                      if (!window.confirm('¿Crear pelea entre estos participantes?')) return;
                      
                      setCreatingMatch(true);
                      try {
                        await axios.post(`/events/${id}/matches/manual`, {
                          fighter1_id: p1.fighter_id,
                          fighter2_id: p2.fighter_id,
                          categoria_edad: p1.categoria_edad,
                          categoria_peso: p1.categoria_peso,
                          genero: p1.genero
                        });
                        toast.success('Pelea creada exitosamente');
                        setSelectedParticipants([]);
                        await load();
                      } catch(e) {
                        toast.error(e?.response?.data?.detail || 'Error al crear la pelea');
                      } finally {
                        setCreatingMatch(false);
                      }
                    }}
                    disabled={creatingMatch}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                  >
                    {creatingMatch ? 'Creando...' : '🥊 Crear Pelea'}
                  </Button>
                  <Button
                    onClick={() => setSelectedParticipants([])}
                    variant="outline"
                    className="text-sm"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            {parts.map(p=> {
              const f = fightersMap[p.fighter_id];
              const tieneCoteja = matches.some(m => 
                m.peleador_rojo_id === p.fighter_id || m.peleador_azul_id === p.fighter_id
              );
              const isSelected = selectedParticipants.includes(p.id);
              
              return (
                <div 
                  key={p.id} 
                  className={`border rounded-lg p-3 flex flex-col gap-2 bg-white shadow-sm transition-all ${
                    isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-zinc-200'
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (selectedParticipants.length < 2) {
                              setSelectedParticipants([...selectedParticipants, p.id]);
                            } else {
                              toast.error('Solo puedes seleccionar 2 participantes');
                            }
                          } else {
                            setSelectedParticipants(selectedParticipants.filter(id => id !== p.id));
                          }
                        }}
                        className="w-4 h-4 text-blue-600 cursor-pointer"
                      />
                      <button
                        onClick={() => {
                          setSelectedFighter(f);
                          setShowFighterModal(true);
                        }}
                        className="font-semibold text-zinc-900 hover:text-blue-600 transition-colors text-left underline decoration-dotted flex-1"
                      >
                        {f ? f.nombre_completo : p.fighter_id}
                      </button>
                    </div>
                    {!tieneCoteja && (
                      <span className="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                        ⚠️ Sin coteja
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-600">
                    {p.categoria_edad} / {p.categoria_peso}
                  </div>
                  <div className="text-xs text-zinc-500">
                    Peso inscripción: <span className="font-semibold text-zinc-700">{p.peso_evento || '—'} kg</span>
                  </div>
                  <div className="mt-1 flex justify-between items-center gap-2">
                    <div className="text-[11px] text-zinc-500">ID participante: {p.id.slice(-6)}</div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="text-xs px-2 py-0.5"
                      onClick={async()=>{
                        if (!window.confirm('¿Eliminar este participante y sus peleas en este evento?')) return;
                        try {
                          await axios.delete(`/events/${id}/participants/${p.id}`);
                          toast.success('Participante eliminado');
                          await load();
                        } catch(e) {
                          toast.error(e?.response?.data?.detail || 'No se pudo eliminar el participante');
                        }
                      }}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Peleas generadas</CardTitle>
          <CardDescription className="text-xs md:text-sm text-zinc-500">Visualiza y registra los resultados de cada pelea por ronda.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {renderMatchesByGroup()}
        </CardContent>
      </Card>

      {/* Podio del torneo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Podio del torneo</CardTitle>
          <CardDescription className="text-xs md:text-sm text-zinc-500">Resumen de posiciones por categoría de peso y modalidad.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {!podioLoaded && (
            <div className="text-xs text-zinc-500">Cargando podio...</div>
          )}
          {podioLoaded && !podio.length && (
            <div className="text-xs text-zinc-500">Aún no hay podio definido. Registra resultados de finales y peleas de tercer lugar.</div>
          )}
          {podioLoaded && podio.length > 0 && (
            <div className="space-y-3">
              {podio.map((row, idx)=>{
                const primero = fightersMap[row.primero_id];
                const segundo = fightersMap[row.segundo_id];
                const tercero = row.tercero_id ? fightersMap[row.tercero_id] : null;
                const tituloCat = `${row.genero || 'Sin género'} • ${row.categoria_edad || 'Sin edad'} / ${row.categoria_peso || 'Sin peso'} (${row.modalidad || ev.modalidad || ''})`;
                const colorClasses = pesoColors[row.categoria_peso] || 'bg-zinc-50 border-zinc-200';
                const peleaLabel = row.modalidad === 'Pelea única'
                  ? `Pelea número ${row.orden_en_ronda || idx + 1}`
                  : null;
                return (
                  <div key={idx} className={`border rounded p-3 ${colorClasses}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold">{tituloCat}</div>
                      {peleaLabel && (
                        <div className="text-[11px] font-medium text-zinc-700 bg-white/60 px-2 py-0.5 rounded-full border border-zinc-200">
                          {peleaLabel}
                        </div>
                      )}
                    </div>
                    <div className="grid md:grid-cols-3 gap-2 text-xs">
                      <div className="flex flex-col items-center text-center p-2 bg-gradient-to-b from-yellow-100 to-yellow-50 rounded-lg border-2 border-yellow-400">
                        <div className="text-5xl mb-1">🥇</div>
                        <div className="font-bold text-sm text-yellow-800 mb-1">1º Lugar</div>
                        <div className="font-semibold text-zinc-900">{primero ? primero.nombre_completo : row.primero_id}</div>
                      </div>
                      <div className="flex flex-col items-center text-center p-2 bg-gradient-to-b from-gray-200 to-gray-50 rounded-lg border-2 border-gray-400">
                        <div className="text-5xl mb-1">🥈</div>
                        <div className="font-bold text-sm text-gray-700 mb-1">2º Lugar</div>
                        <div className="font-semibold text-zinc-900">{segundo ? segundo.nombre_completo : row.segundo_id}</div>
                      </div>
                      {row.modalidad === 'Eliminatorias' && (
                        <div className="flex flex-col items-center text-center p-2 bg-gradient-to-b from-orange-200 to-orange-50 rounded-lg border-2 border-orange-400">
                          <div className="text-5xl mb-1">🥉</div>
                          <div className="font-bold text-sm text-orange-800 mb-1">3º Lugar</div>
                          <div className="font-semibold text-zinc-900">{tercero ? tercero.nombre_completo : (row.tercero_id || '—')}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de detalles del peleador */}
      {showFighterModal && selectedFighter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowFighterModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-zinc-900">Información del Peleador</h2>
              <button
                onClick={() => setShowFighterModal(false)}
                className="text-zinc-500 hover:text-zinc-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Nombre completo</label>
                  <p className="text-base text-zinc-900">{selectedFighter.nombre_completo}</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Número de identificación</label>
                  <p className="text-base text-zinc-900">{selectedFighter.numero_identificacion || '—'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Fecha de nacimiento</label>
                  <p className="text-base text-zinc-900">{selectedFighter.fecha_nacimiento || '—'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Edad competitiva</label>
                  <p className="text-base text-zinc-900">{selectedFighter.edad_calculada || '—'} años</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Género</label>
                  <p className="text-base text-zinc-900">{selectedFighter.genero || '—'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Peso actual</label>
                  <p className="text-base text-zinc-900">{selectedFighter.peso_kg || '—'} kg</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Categoría de edad</label>
                  <p className="text-base text-zinc-900">{selectedFighter.categoria_edad || '—'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Categoría de peso</label>
                  <p className="text-base text-zinc-900">{selectedFighter.categoria_peso || '—'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Club representado</label>
                  <p className="text-base text-zinc-900">{selectedFighter.club_representado || '—'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-zinc-700">Total de peleas</label>
                  <p className="text-base font-semibold text-blue-600">{selectedFighter.numero_total_peleas || 0}</p>
                </div>
                
                <div className="md:col-span-2">
                  <label className="text-sm font-semibold text-zinc-700">Contacto</label>
                  <p className="text-base text-zinc-900">{selectedFighter.contacto || '—'}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-zinc-200">
                <Button
                  onClick={() => setShowFighterModal(false)}
                  className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const FighterEdit = () => {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const load = async()=>{ const { data } = await axios.get(`/fighters/${id}`); setF(data); };
  useEffect(()=>{ load(); },[id]);
  if (!f) return null;
  const setDate = (y,m,d)=> setF({...f, fecha_nacimiento:`${y}-${m}-${d}`});
  const onSave = async()=>{
    try{ await axios.patch(`/fighters/${id}`, { nombre_completo:f.nombre_completo, fecha_nacimiento:f.fecha_nacimiento, peso_kg:f.peso_kg, contacto:f.contacto, numero_total_peleas:f.numero_total_peleas, genero:f.genero, club_representado:f.club_representado }); toast.success('Peleador actualizado'); }
    catch(e){ toast.error(e?.response?.data?.detail || 'Error al actualizar'); }
  };
  const [Y,M,D] = f.fecha_nacimiento.split('-');
  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4">
      <h1 className="text-2xl font-semibold">Editar peleador</h1>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium">Nombre completo</label>
          <Input value={f.nombre_completo} onChange={(e)=>setF({...f, nombre_completo:e.target.value})} />
        </div>
        <div>
          <label className="text-sm font-medium">Club / Gimnasio que representa</label>
          <Input value={f.club_representado || ''} onChange={(e)=>setF({...f, club_representado:e.target.value})} />
        </div>
        <div>
          <label className="text-sm font-medium">Fecha de nacimiento (DD/MM/AAAA)</label>
          <div className="flex gap-2">
            <select value={D} onChange={(e)=>setDate(Y,M,e.target.value.padStart(2,'0'))} className="border rounded px-2 py-1">{Array.from({length:31},(_,i)=>String(i+1).padStart(2,'0')).map(d=> <option key={d} value={d}>{d}</option>)}</select>
            <select value={M} onChange={(e)=>setDate(Y,e.target.value,D)} className="border rounded px-2 py-1">{Array.from({length:12},(_,i)=>String(i+1).padStart(2,'0')).map(m=> <option key={m} value={m}>{m}</option>)}</select>
            <select value={Y} onChange={(e)=>setDate(e.target.value,M,D)} className="border rounded px-2 py-1">{Array.from({length:60},(_,i)=>String(1965+i)).map(y=> <option key={y} value={y}>{y}</option>)}</select>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Peso (kg)</label>
          <Input value={f.peso_kg} onChange={(e)=>setF({...f, peso_kg:parseFloat(e.target.value||0)})} />
        </div>
        <div>
          <label className="text-sm font-medium">Género</label>
          <select className="border rounded px-2 py-2 w-full" value={f.genero} onChange={(e)=>setF({...f, genero:e.target.value})}><option>Masculino</option><option>Femenino</option><option>Otro</option></select>
        </div>
        <div>
          <label className="text-sm font-medium">Contacto</label>
          <Input value={f.contacto||''} onChange={(e)=>setF({...f, contacto:e.target.value})} />
        </div>
        <div>
          <label className="text-sm font-medium">Total de peleas</label>
          <Input value={f.numero_total_peleas} onChange={(e)=>setF({...f, numero_total_peleas:parseInt(e.target.value||0)})} />
        </div>
      </div>
      <Button data-testid="fighter-save" onClick={onSave} className="bg-black text-white">Guardar</Button>
    </div>
  );
};

const FightersAdminTable = () => {
  const [fighters, setFighters] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [qName, setQName] = useState("");
  const [qId, setQId] = useState("");

  useEffect(()=>{ (async()=>{
    try {
      const { data } = await axios.get(`/fighters`);
      setFighters(data);
      setFiltered(data);
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar peleadores');
    }
  })(); },[]);

  const filtrar = ()=>{
    let res = fighters;
    if (qName.trim()){
      const q = qName.trim().toLowerCase();
      res = res.filter(f=> (f.nombre_completo||"").toLowerCase().includes(q));
    }
    if (qId.trim()){
      const q = qId.trim();
      res = res.filter(f=> String(f.numero_identificacion||f.numeroIdentificacion||"") === q);
    }
    setFiltered(res);
  };

  useEffect(()=>{ filtrar(); }, [qName, qId, fighters]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 mb-2">
        <Input placeholder="Buscar por nombre" value={qName} onChange={(e)=>setQName(e.target.value)} className="max-w-xs" />
        <Input placeholder="Buscar por número de ID" value={qId} onChange={(e)=>setQId(e.target.value)} className="max-w-xs" />
      </div>
      <div className="overflow-x-auto max-h-96 border border-zinc-200 rounded-lg bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-zinc-100">
              <th className="px-2 py-1 text-left">Nombre</th>
              <th className="px-2 py-1 text-left">N° identificación</th>
              <th className="px-2 py-1 text-left">Club / Gimnasio que representa</th>
              <th className="px-2 py-1 text-left">Ranking</th>
              <th className="px-2 py-1 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(f=> (
              <AdminFighterRow key={f.id} fighter={f} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminFighterRow = ({ fighter }) => {
  const [rank, setRank] = useState(null);

  useEffect(()=>{
    (async()=>{
      try {
        const categoriaEdad = fighter.categoria_edad;
        const categoriaPeso = fighter.categoria_peso || 'Absoluta';
        let list = [];
        if (categoriaEdad && categoriaPeso){
          const { data } = await axios.get(`/rankings/by-category`, { params: { categoria_edad: categoriaEdad, categoria_peso: categoriaPeso } });
          list = data || [];
        } else {
          const { data } = await axios.get(`/rankings/absolute`);
          list = data || [];
        }
        const idx = list.findIndex(r=> r.peleador_id === fighter.id);
        setRank(idx >= 0 ? idx+1 : null);
      } catch(e) {
        setRank(null);
      }
    })();
  }, [fighter.id]);

  const borrar = async()=>{
    if (!window.confirm('¿Eliminar peleador? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`/fighters/${fighter.id}`);
      toast.success('Peleador eliminado');
      window.location.reload();
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  return (
    <tr className="odd:bg-white even:bg-zinc-50">
      <td className="px-2 py-1 font-medium text-zinc-900">
        {fighter.nombre_completo}
      </td>
      <td className="px-2 py-1">{fighter.numero_identificacion || fighter.numeroIdentificacion}</td>
      <td className="px-2 py-1">{fighter.club_representado || '—'}</td>
      <td className="px-2 py-1">{rank || '—'}</td>
      <td className="px-2 py-1 space-x-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs px-2 py-0.5 border-zinc-300 text-zinc-800 hover:bg-zinc-100"
          onClick={()=>{ window.location.href = `/fighters/${fighter.id}/edit`; }}
        >
          Editar
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          className="text-xs px-2 py-0.5"
          onClick={borrar}
        >
          Eliminar
        </Button>
      </td>
    </tr>
  );
};

const GymsTable = () => {
  const [gyms, setGyms] = useState([]);
  const [loaded, setLoaded] = useState(false);
  
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`/gyms`);
        setGyms(data);
        setLoaded(true);
      } catch(e) {
        toast.error('Error al cargar gimnasios');
      }
    })();
  }, []);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">🏋️ Gimnasios Registrados</CardTitle>
        <CardDescription className="text-xs md:text-sm text-zinc-500">Lista completa de todos los gimnasios en el sistema.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100">
              <tr>
                <th className="p-2 text-left">N°</th>
                <th className="p-2 text-left">Nombre</th>
                <th className="p-2 text-left">Ciudad</th>
                <th className="p-2 text-left">País</th>
                <th className="p-2 text-left">Teléfono</th>
                <th className="p-2 text-left">Email</th>
                <th className="p-2 text-left">Estado</th>
              </tr>
            </thead>
            <tbody>
              {!loaded ? (
                <tr>
                  <td colSpan="7" className="p-4 text-center text-zinc-500">Cargando...</td>
                </tr>
              ) : gyms.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-4 text-center text-zinc-500">No hay gimnasios registrados</td>
                </tr>
              ) : (
                gyms.map((gym, idx) => (
                  <tr key={gym.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                    <td className="p-2">{idx + 1}</td>
                    <td className="p-2 font-medium">
                      {gym.logo_url && (
                        <img src={gym.logo_url} alt={gym.nombre_gimnasio} className="inline-block w-6 h-6 rounded mr-2 object-cover" />
                      )}
                      {gym.nombre_gimnasio || gym.nombre || '—'}
                    </td>
                    <td className="p-2">{gym.ciudad || '—'}</td>
                    <td className="p-2">{gym.pais || '—'}</td>
                    <td className="p-2">{gym.telefono || '—'}</td>
                    <td className="p-2 text-xs">{gym.email_contacto || '—'}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${gym.activo === false ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {gym.activo === false ? 'Inactivo' : 'Activo'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {loaded && (
            <div className="mt-3 text-xs text-zinc-600">
              Total: <strong>{gyms.length}</strong> gimnasios registrados
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Admin = () => {
  const [form, setForm] = useState({ nombre_gimnasio:"", direccion:"", ciudad:"", pais:"", telefono:"", logo_archivo:null, email_usuario:"", password_usuario:"" });
  const createGymWithUser = async () => {
    try {
      // Primero crear gimnasio + usuario
      const { data } = await axios.post(`/admin/gyms-with-user`, form);
      const gymId = data.gym_id;

      // Si hay archivo de logo, subirlo
      if (form.logo_archivo && gymId){
        const fd = new FormData();
        fd.append('file', form.logo_archivo);
        await axios.post(`/gyms/${gymId}/logo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success("Gimnasio + usuario creados");
      setForm({ nombre_gimnasio:"", direccion:"", ciudad:"", pais:"", telefono:"", logo_archivo:null, email_usuario:"", password_usuario:"" });
    } catch(e){ const err=e?.response?.data; const msg=(typeof err?.detail==='string')?err.detail:(err?.detail?JSON.stringify(err.detail):(typeof err==='string'?err:'Error en creación unificada')); toast.error(msg); }
  };
  const [tabla, setTabla] = useState([]);
  useEffect(()=>{ (async()=>{ 
    try {
      const userData = localStorage.getItem('hiit_user');
      const token = userData ? JSON.parse(userData).access_token : null;
      const { data } = await axios.get(`/config/tabla-puntaje`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      }); 
      setTabla(data); 
    } catch(e) {
      console.error('Error cargando tabla:', e);
    }
  })(); },[]);
  const saveTabla = async ()=>{ 
    const userData = localStorage.getItem('hiit_user');
    const token = userData ? JSON.parse(userData).access_token : null;
    await axios.put(`/config/tabla-puntaje`, tabla, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    }); 
    toast.success("Puntajes guardados"); 
  };

  // Centro de control de usuarios
  const [users, setUsers] = useState([]);
  const [gymAdmins, setGymAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedPayload, setSelectedPayload] = useState({ email:"", password:"", gym:{} });
  const [gymLogoFile, setGymLogoFile] = useState(null);

  useEffect(()=>{ (async()=>{ 
    try {
      const userData = localStorage.getItem('hiit_user');
      const token = userData ? JSON.parse(userData).access_token : null;
      const { data } = await axios.get(`/config/tabla-puntaje`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      }); 
      setTabla(data); 
    } catch(e) {
      console.error('Error cargando tabla:', e);
    }
  })(); },[]);
  useEffect(()=>{ (async()=>{
    try {
      const userData = localStorage.getItem('hiit_user');
      const token = userData ? JSON.parse(userData).access_token : null;
      const { data } = await axios.get(`/admin/users`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setUsers(data);
    } catch(e) {
      console.error('Error cargando usuarios:', e);
    }
  })(); },[]);

  useEffect(()=>{ (async()=>{
    try {
      const userData = localStorage.getItem('hiit_user');
      const token = userData ? JSON.parse(userData).access_token : null;
      const { data } = await axios.get(`/admin/gym-admins`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setGymAdmins(data);
    } catch(e) {
      console.error('Error cargando gym admins:', e);
    }
  })(); },[]);

  const openAdminDetail = async (id)=>{
    try {
      const userData = localStorage.getItem('hiit_user');
      const token = userData ? JSON.parse(userData).access_token : null;
      const { data } = await axios.get(`/admin/gym-admins/${id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setSelectedAdmin(data);
      setSelectedPayload({
        email: data.email || "",
        password: "",
        gym: {
          nombre_gimnasio: data.gym?.nombre_gimnasio || "",
          direccion: data.gym?.direccion || "",
          ciudad: data.gym?.ciudad || "",
          pais: data.gym?.pais || "",
          telefono: data.gym?.telefono || "",
          email_contacto: data.gym?.email_contacto || "",
          logo_url: data.gym?.logo_url || "",
          activo: data.gym?.activo ?? true,
        }
      });
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el Gym Admin');
    }
  };

  const saveAdminDetail = async ()=>{
    if (!selectedAdmin) return;
    try {
      const userData = localStorage.getItem('hiit_user');
      const token = userData ? JSON.parse(userData).access_token : null;
      await axios.patch(`/admin/gym-admins/${selectedAdmin.id}`, selectedPayload, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      toast.success('Datos de Gym Admin actualizados');
      // refrescar lista
      const { data } = await axios.get(`/admin/gym-admins`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setGymAdmins(data);
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-8">
      <div className="flex items-center justify-between border-b border-zinc-200 pb-4 mb-2">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-900">Panel Admin</h1>
          <p className="text-xs md:text-sm text-zinc-500">Administra gimnasios, usuarios y la tabla de puntaje del sistema.</p>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Crear usuario de gimnasio</CardTitle>
          <CardDescription className="text-xs md:text-sm text-zinc-500">Crea un gimnasio y su usuario administrador de una sola vez.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-3">
          <Input data-testid="newgym-nombre" placeholder="Nombre del gimnasio" value={form.nombre_gimnasio} onChange={(e)=>setForm({...form, nombre_gimnasio:e.target.value})} />
          <Input placeholder="Dirección" value={form.direccion||""} onChange={(e)=>setForm({...form, direccion:e.target.value})} />
          <Input placeholder="Ciudad" value={form.ciudad||""} onChange={(e)=>setForm({...form, ciudad:e.target.value})} />
          <Input placeholder="País" value={form.pais||""} onChange={(e)=>setForm({...form, pais:e.target.value})} />
          <Input placeholder="Teléfono" value={form.telefono||""} onChange={(e)=>setForm({...form, telefono:e.target.value})} />
          <div className="flex flex-col gap-1 text-xs text-zinc-700">
            <span>Logo del gimnasio (JPG o PNG)</span>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={(e)=>{
                const file = e.target.files?.[0];
                if (!file) return;
                if (!['image/png','image/jpeg'].includes(file.type)){
                  toast.error('Solo se aceptan archivos JPG o PNG');
                  return;
                }
                setForm({...form, logo_archivo:file});
              }}
              className="border rounded px-2 py-1 text-xs bg-white"
            />
            <span className="text-[11px] text-zinc-500">Formatos soportados: JPG y PNG. Tamaño recomendado aprox. 200x200px.</span>
          </div>
          <Input data-testid="newgym-email" placeholder="Email usuario" value={form.email_usuario} onChange={(e)=>setForm({...form, email_usuario:e.target.value})} />
          <Input data-testid="newgym-pass" placeholder="Contraseña usuario" value={form.password_usuario} onChange={(e)=>setForm({...form, password_usuario:e.target.value})} />
          <Button data-testid="newgym-submit" onClick={createGymWithUser} className="bg-black text-white">Crear</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Tabla de usuarios</CardTitle>
          <CardDescription className="text-xs md:text-sm text-zinc-500">Listado de todos los usuarios históricos con control de estado y eliminación.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-zinc-100">
                  <th className="px-2 py-1 text-left">Email</th>
                  <th className="px-2 py-1 text-left">Rol</th>
                  <th className="px-2 py-1 text-left">Nombre del gimnasio</th>
                  <th className="px-2 py-1 text-left">Estado</th>
                  <th className="px-2 py-1 text-left">Acciones</th>
                  <th className="px-2 py-1 text-left">Editar gimnasio</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="odd:bg-white even:bg-zinc-50">
                    <td className="px-2 py-1">{u.email}</td>
                    <td className="px-2 py-1">{u.role}</td>
                    <td className="px-2 py-1">{u.nombre_gimnasio || '—'}</td>
                    <td className="px-2 py-1">{u.activo ? 'Activo' : 'Desactivado'}</td>
                    <td className="px-2 py-1 space-x-2">
                      <button
                        className={`${u.activo ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-600 hover:bg-green-700'} text-white text-xs px-2 py-0.5 rounded`}
                        onClick={async()=>{
                          try {
                            await axios.patch(`/admin/users/${u.id}`, { activo: !u.activo });
                            toast.success('Estado actualizado');
                            const { data } = await axios.get(`/admin/users`);
                            setUsers(data);
                          } catch(e) {
                            toast.error(e?.response?.data?.detail || 'No se pudo actualizar');
                          }
                        }}
                      >
                        {u.activo ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        className="bg-red-600 text-white text-xs px-2 py-0.5 rounded"
                        onClick={async()=>{
                          if (!window.confirm('¿Eliminar este usuario?')) return;
                          try {
                            await axios.delete(`/admin/users/${u.id}`);
                            toast.success('Usuario eliminado');
                            const { data } = await axios.get(`/admin/users`);
                            setUsers(data);
                          } catch(e) {
                            toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
                          }
                        }}
                      >
                        Eliminar
                      </button>
                    </td>
                    <td className="px-2 py-1">
                      {u.role === 'gym_admin' && (
                        <button
                          className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded"
                          onClick={()=>openAdminDetail(u.id)}
                        >
                          Editar gimnasio
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {selectedAdmin && (
        <Card>
          <CardHeader><CardTitle>Editar gimnasio seleccionado</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium">Nombre gimnasio</label>
                <Input
                  value={selectedPayload.gym.nombre_gimnasio}
                  onChange={(e)=>setSelectedPayload({...selectedPayload, gym:{...selectedPayload.gym, nombre_gimnasio:e.target.value}})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Dirección</label>
                <Input
                  value={selectedPayload.gym.direccion || ''}
                  onChange={(e)=>setSelectedPayload({...selectedPayload, gym:{...selectedPayload.gym, direccion:e.target.value}})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Ciudad</label>
                <Input
                  value={selectedPayload.gym.cuidad || ''}
                  onChange={(e)=>setSelectedPayload({...selectedPayload, gym:{...selectedPayload.gym, ciudad:e.target.value}})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">País</label>
                <Input
                  value={selectedPayload.gym.pais || ''}
                  onChange={(e)=>setSelectedPayload({...selectedPayload, gym:{...selectedPayload.gym, pais:e.target.value}})}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Teléfono</label>
                <Input
                  value={selectedPayload.gym.telefono || ''}
                  onChange={(e)=>setSelectedPayload({...selectedPayload, gym:{...selectedPayload.gym, telefono:e.target.value}})}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              {selectedPayload.gym.logo_url ? (
                <img
                  src={selectedPayload.gym.logo_url}
                  alt={selectedPayload.gym.nombre_gimnasio || 'Logo gimnasio'}
                  className="h-16 w-16 rounded-full object-cover border"
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-zinc-200 flex items-center justify-center text-xs text-zinc-500">
                  Sin logo
                </div>
              )}
              <div className="flex flex-col gap-1 text-xs text-zinc-700">
                <span>Actualizar logo (JPG o PNG)</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e)=>{
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!['image/png','image/jpeg'].includes(file.type)){
                      toast.error('Solo se aceptan archivos JPG o PNG');
                      return;
                    }
                    setGymLogoFile(file);
                  }}
                  className="border rounded px-2 py-1 text-xs bg-white"
                />
                <span className="text-[11px] text-zinc-500">Formatos soportados: JPG y PNG. Tamaño recomendado aprox. 200x200px.</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-yellow-400 text-black" onClick={saveAdminDetail}>
                Guardar datos de gimnasio
              </Button>
              <Button
                size="sm"
                className="bg-black text-white"
                onClick={async()=>{
                  if (!gymLogoFile){
                    toast.error('Selecciona un archivo de logo primero');
                    return;
                  }
                  try {
                    const fd = new FormData();
                    fd.append('file', gymLogoFile);
                    await axios.post(`/gyms/${selectedAdmin.gimnasio_id}/logo`, fd, {
                      headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    toast.success('Logo actualizado');
                    const { data } = await axios.get(`/admin/gym-admins/${selectedAdmin.id}`);
                    setSelectedAdmin(data);
                    setSelectedPayload({
                      email: data.email || '',
                      password: '',
                      gym: {
                        nombre_gimnasio: data.gym?.nombre_gimnasio || '',
                        direccion: data.gym?.direccion || '',
                        ciudad: data.gym?.ciudad || '',
                        pais: data.gym?.pais || '',
                        telefono: data.gym?.telefono || '',
                        email_contacto: data.gym?.email_contacto || '',
                        logo_url: data.gym?.logo_url || '',
                        activo: data.gym?.activo ?? true,
                      },
                    });
                    setGymLogoFile(null);
                  } catch(e) {
                    toast.error(e?.response?.data?.detail || 'No se pudo actualizar el logo');
                  }
                }}
              >
                Guardar logo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <GymsTable />

      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Base de datos de peleadores</CardTitle>
          <CardDescription className="text-xs md:text-sm text-zinc-500">Vista general de todos los peleadores registrados en el sistema.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <FightersAdminTable />
        </CardContent>
      </Card>


      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Tabla de puntaje</CardTitle>
          <CardDescription className="text-xs md:text-sm text-zinc-500">Configura los puntos otorgados según el tipo de resultado para el ranking.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {tabla.map((t, idx)=> (
            <div key={idx} className="grid grid-cols-3 gap-2">
              <Input value={t.tipo_resultado} readOnly />
              <Input value={t.puntos_ganador} onChange={(e)=>{ const v=[...tabla]; v[idx].puntos_ganador=parseFloat(e.target.value||0); setTabla(v); }} />
              <Input value={t.puntos_oponente} onChange={(e)=>{ const v=[...tabla]; v[idx].puntos_oponente=parseFloat(e.target.value||0); setTabla(v); }} />
            </div>
          ))}
          <Button data-testid="admin-save-score" onClick={saveTabla} className="bg-yellow-400 text-black">Guardar</Button>
        </CardContent>
      </Card>
    </div>
  );
};

const Affiliate = ({ user }) => {
  const gymId = user?.user?.gimnasio_id;
  const [gymName, setGymName] = useState("");
  const [fighter, setFighter] = useState({ nombre_completo:"", numero_identificacion:"", fecha_nacimiento:"2008-01-01", peso_kg:60, contacto:"", numero_total_peleas:0, genero:"Masculino", gimnasio_id:gymId, club_representado:""});
  const [events, setEvents] = useState([]);
  useEffect(()=>{ (async()=>{
    if (!gymId) return;
    const { data } = await axios.get(`/events`, { params: { gym_id:gymId }}); setEvents(data);
    try {
      const gymsRes = await axios.get(`/gyms`);
      const g = gymsRes.data.find(g=>g.id === gymId);
      if (g) setGymName(g.nombre_gimnasio || "");
    } catch(e) {
      // ignore
    }
  })(); },[gymId]);

  const [eventForm, setEventForm] = useState({ nombre_competencia:"", fecha_competencia:"2025-12-01", gimnasio_organizador_id:gymId, pais:"", ciudad:"", direccion:"", modalidad:"Eliminatorias", ubicacion:"" , estado:"Programada"});
  const createEvent = async ()=>{ try{ await axios.post(`/events`, eventForm); toast.success("Evento creado"); const { data } = await axios.get(`/events`, { params: { gym_id:gymId }}); setEvents(data);} catch(e){ toast.error(e?.response?.data?.detail || "Error evento"); } };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-8">
      <div className="flex items-center justify-between border-b border-zinc-200 pb-4 mb-2">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-900">Panel Gimnasio</h1>
          <p className="text-xs md:text-sm text-zinc-500">Gestiona tus eventos y registra peleadores para {gymName || 'tu gimnasio'}.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Crear evento</CardTitle>
          <CardDescription className="text-xs md:text-sm text-zinc-500">Define los datos básicos del evento y su modalidad de pelea.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <Input
              data-testid="event-nombre"
              placeholder="Nombre del evento"
              value={eventForm.nombre_competencia}
              onChange={(e)=>setEventForm({...eventForm, nombre_competencia:e.target.value})}
              className="md:col-span-2"
            />
            <div>
              <label className="text-sm font-medium">Fecha (DD/MM/AAAA)</label>
              <div className="flex gap-2 mt-1">
                <select
                  data-testid="event-dia"
                  className="border rounded px-2 py-1"
                  value={(eventForm.fecha_competencia.split('-')[2]||'01')}
                  onChange={(e)=>{ const [y,m]=eventForm.fecha_competencia.split('-'); setEventForm({...eventForm, fecha_competencia:`${y||'2025'}-${m||'01'}-${e.target.value.padStart(2,'0')}`}); }}
                >
                  {Array.from({length:31},(_,i)=>String(i+1).padStart(2,'0')).map(d=> <option key={d} value={d}>{d}</option>)}
                </select>
                <select
                  data-testid="event-mes"
                  className="border rounded px-2 py-1"
                  value={(eventForm.fecha_competencia.split('-')[1]||'01')}
                  onChange={(e)=>{ const [y,,d]=eventForm.fecha_competencia.split('-'); setEventForm({...eventForm, fecha_competencia:`${y||'2025'}-${e.target.value}-${d||'01'}`}); }}
                >
                  {Array.from({length:12},(_,i)=>String(i+1).padStart(2,'0')).map(m=> <option key={m} value={m}>{m}</option>)}
                </select>
                <select
                  data-testid="event-anio"
                  className="border rounded px-2 py-1"
                  value={(eventForm.fecha_competencia.split('-')[0]||'2025')}
                  onChange={(e)=>{ const [,m,d]=eventForm.fecha_competencia.split('-'); setEventForm({...eventForm, fecha_competencia:`${e.target.value}-${m||'01'}-${d||'01'}`}); }}
                >
                  {Array.from({length:10},(_,i)=>String(2024+i)).map(y=> <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <Input data-testid="event-pais" placeholder="País" value={eventForm.pais} onChange={(e)=>setEventForm({...eventForm, pais:e.target.value})} />
            <Input data-testid="event-ciudad" placeholder="Ciudad" value={eventForm.ciudad} onChange={(e)=>setEventForm({...eventForm, ciudad:e.target.value})} />
            <Input data-testid="event-direccion" placeholder="Dirección" value={eventForm.direccion} onChange={(e)=>setEventForm({...eventForm, direccion:e.target.value})} />
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium">Modalidad de pelea</label>
              <select
                data-testid="event-modalidad"
                className="border rounded px-2 py-1 w-full mt-1"
                value={eventForm.modalidad}
                onChange={(e)=>setEventForm({...eventForm, modalidad:e.target.value})}
              >
                <option>Eliminatorias</option>
                <option>Pelea única</option>
              </select>
            </div>
            <Input
              data-testid="event-ubicacion"
              placeholder="Ubicación (nombre del local o dirección para Google Maps)"
              value={eventForm.ubicacion}
              onChange={(e)=>setEventForm({...eventForm, ubicacion:e.target.value})}
            />
            <Button
              data-testid="event-crear"
              onClick={createEvent}
              className="bg-yellow-400 text-black"
            >
              Crear Evento
            </Button>
          </div>

          <div className="pt-3 border-t text-sm text-zinc-600 space-y-2">
            <div className="font-medium">Mis eventos</div>
            {events.length === 0 && (
              <div className="text-xs text-zinc-500">Aún no has creado eventos para este gimnasio.</div>
            )}
            {events.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {events.map(e=> (
                  <Card key={e.id} className="border border-zinc-200 bg-white shadow-sm">
                    <CardContent className="p-3 space-y-1">
                      <div className="text-xs uppercase tracking-wide text-zinc-500">Evento</div>
                      <div className="font-semibold text-sm text-zinc-900">{e.nombre_competencia}</div>
                      <div className="text-xs text-zinc-600">{e.fecha_competencia} • {e.ciudad || '—'}</div>
                      <Button
                        type="button"
                        size="sm"
                        className="mt-2 w-full bg-yellow-400 text-black hover:bg-yellow-300 text-xs"
                        onClick={()=>{ window.location.href = `/events/${e.id}`; }}
                      >
                        Ver detalle
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base md:text-lg font-semibold text-zinc-900">Registrar peleador (opcional)</CardTitle>
          <CardDescription className="text-xs md:text-sm text-zinc-500">Registra nuevos peleadores asociados a tu gimnasio.</CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-sm font-medium">Club / Gimnasio que representa</label>
            <Input data-testid="fighter-club" placeholder="Nombre del club/gimnasio" value={fighter.club_representado || ""} onChange={(e)=>setFighter({...fighter, club_representado:e.target.value})} />
          </div>
          <div>
            <label className="text-sm font-medium">Nombre completo</label>
            <Input data-testid="fighter-nombre" placeholder="Ej: Juan Pérez" value={fighter.nombre_completo} onChange={(e)=>setFighter({...fighter, nombre_completo:e.target.value})} />
          </div>
          <div>
            <label className="text-sm font-medium">Número de identificación</label>
            <Input data-testid="fighter-id" placeholder="ID único" value={fighter.numero_identificacion} onChange={(e)=>setFighter({...fighter, numero_identificacion:e.target.value})} />
          </div>
          <div>
            <label className="text-sm font-medium">Fecha de nacimiento (DD/MM/AAAA)</label>
            <div className="flex gap-2">
              <select data-testid="fighter-dia" className="border rounded px-2 py-1" value={fighter.fecha_nacimiento.split('-')[2]||'01'} onChange={(e)=>{ const [y,m]=fighter.fecha_nacimiento.split('-'); setFighter({...fighter, fecha_nacimiento:`${y||'2008'}-${m||'01'}-${e.target.value.padStart(2,'0')}`}); }}>
                {Array.from({length:31},(_,i)=>String(i+1).padStart(2,'0')).map(d=> <option key={d} value={d}>{d}</option>)}
              </select>
              <select data-testid="fighter-mes" className="border rounded px-2 py-1" value={fighter.fecha_nacimiento.split('-')[1]||'01'} onChange={(e)=>{ const [y,,d]=fighter.fecha_nacimiento.split('-'); setFighter({...fighter, fecha_nacimiento:`${y||'2008'}-${e.target.value}-${d||'01'}`}); }}>
                {Array.from({length:12},(_,i)=>String(i+1).padStart(2,'0')).map(m=> <option key={m} value={m}>{m}</option>)}
              </select>
              <select data-testid="fighter-anio" className="border rounded px-2 py-1" value={fighter.fecha_nacimiento.split('-')[0]||'2008'} onChange={(e)=>{ const [,m,d]=fighter.fecha_nacimiento.split('-'); setFighter({...fighter, fecha_nacimiento:`${e.target.value}-${m||'01'}-${d||'01'}`}); }}>
                {Array.from({length:60},(_,i)=>String(1965+i)).map(y=> <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Peso (kg)</label>
            <Input data-testid="fighter-peso" placeholder="Ej: 60" value={fighter.peso_kg} onChange={(e)=>setFighter({...fighter, peso_kg:parseFloat(e.target.value||0)})} />
          </div>
          <div>
            <label className="text-sm font-medium">Género</label>
            <select data-testid="fighter-genero" className="border rounded px-2 py-2 w-full" value={fighter.genero} onChange={(e)=>setFighter({...fighter, genero:e.target.value})}>
              <option>Masculino</option>
              <option>Femenino</option>
              <option>Otro</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Contacto</label>
            <Input data-testid="fighter-contacto" placeholder="Celular/Teléfono" value={fighter.contacto} onChange={(e)=>setFighter({...fighter, contacto:e.target.value})} />
          </div>
          <div>
            <label className="text-sm font-medium">Total de peleas</label>
            <Input data-testid="fighter-peleas" placeholder="0" value={fighter.numero_total_peleas} onChange={(e)=>setFighter({...fighter, numero_total_peleas:parseInt(e.target.value||0)})} />
          </div>
          <div className="col-span-full flex justify-end">
            <Button
              data-testid="fighter-crear"
              onClick={async()=>{
                if(!fighter.nombre_completo||!fighter.numero_identificacion||!fighter.fecha_nacimiento){ toast.error('Completa los campos obligatorios'); return;}
                try{
                  await axios.post(`/fighters`, fighter);
                  toast.success('Peleador creado');
                } catch(e){
                  toast.error(e?.response?.data?.detail||'Error');
                }
              }}
              className="bg-black text-white"
            >
              Crear peleador
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Search = () => {
  const [qName, setQName] = useState("");
  const [qId, setQId] = useState("");
  const [items, setItems] = useState([]);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(()=>{ (async()=>{
    try {
      const { data } = await axios.get(`/fighters`);
      setAll(data);
      setItems(data);
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar peleadores');
    }
  })(); },[]);

  const filtrar = ()=>{
    let res = all;
    if (qName.trim()){
      const q = qName.trim().toLowerCase();
      res = res.filter(f=> (f.nombre_completo||"").toLowerCase().includes(q));
    }
    if (qId.trim()){
      const q = qId.trim();
      res = res.filter(f=> String(f.numero_identificacion||f.numeroIdentificacion||"") === q);
    }
    setItems(res);
  };

  useEffect(()=>{ filtrar(); }, [qName, qId, all]);

  const cargarRanking = async (fighter) => {
    // Nota: este setLoading pertenece al componente Search, por lo que solo se usa allí
    setLoading(true);
    try {
      const categoriaEdad = fighter.categoria_edad;
      const categoriaPeso = fighter.categoria_peso || 'Absoluta';
      let list = [];
      if (categoriaEdad && categoriaPeso){
        const { data } = await axios.get(`/rankings/by-category`, { params: { categoria_edad: categoriaEdad, categoria_peso: categoriaPeso } });
        list = data || [];
      } else {
        const { data } = await axios.get(`/rankings/absolute`);
        list = data || [];
      }
      const idx = list.findIndex(r=> r.peleador_id === fighter.id);
      return idx >= 0 ? idx+1 : null;
    } catch(e) {
      return null;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between border-b border-zinc-200 pb-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-900">Buscar peleador</h1>
          <p className="text-xs md:text-sm text-zinc-500">Filtra por nombre o número de identificación para ver su categoría y ranking.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input placeholder="Buscar por nombre" value={qName} onChange={(e)=>setQName(e.target.value)} className="max-w-xs" />
        <Input placeholder="Buscar por número de ID" value={qId} onChange={(e)=>setQId(e.target.value)} className="max-w-xs" />
      </div>
      <div className="rounded-lg border border-zinc-200 overflow-hidden mt-4 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-100">
            <tr>
              <th className="p-2">Nombre</th>
              <th className="p-2">N° identificación</th>
              <th className="p-2">Categoría edad</th>
              <th className="p-2">Categoría peso</th>
              <th className="p-2">Ranking</th>
              <th className="p-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map(f=> (
              <SearchRow key={f.id} fighter={f} cargarRanking={cargarRanking} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SearchRow = ({ fighter, cargarRanking }) => {
  const [rank, setRank] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(()=>{ (async()=>{
    setLoading(true);
    const r = await cargarRanking(fighter);
    setRank(r);
    setLoading(false);
  })(); }, [fighter.id]);

  const auth = useAuth();
  const canDelete = auth.user && auth.user.user.role === 'admin';

  const borrar = async()=>{
    if (!window.confirm('¿Eliminar peleador? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`/fighters/${fighter.id}`);
      toast.success('Peleador eliminado');
      window.location.reload();
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  return (
    <tr className="odd:bg-white even:bg-zinc-50">
      <td className="p-2 font-medium text-zinc-900">
        {fighter.nombre_completo}
      </td>
      <td className="p-2">{fighter.numero_identificacion || fighter.numeroIdentificacion}</td>
      <td className="p-2">{fighter.categoria_edad || '—'}</td>
      <td className="p-2">{fighter.categoria_peso || '—'}</td>
      <td className="p-2">{loading ? '...' : (rank || '—')}</td>
      <td className="p-2 space-x-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs px-2 py-0.5 border-zinc-300 text-zinc-800 hover:bg-zinc-100"
          onClick={()=>{ window.location.href = `/fighters/${fighter.id}/edit`; }}
        >
          Editar
        </Button>
        {canDelete && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="text-xs px-2 py-0.5"
            onClick={borrar}
          >
            Eliminar
          </Button>
        )}
      </td>
    </tr>
  );
};

const Protected = ({ user, roles, children }) => {
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.user.role)) return <Navigate to="/" replace />;
  return children;
};

// =====================================================
// PUBLIC REGISTRATION PAGE - Formulario público
// =====================================================

const PublicRegistration = () => {
  const { slug } = useParams();
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    nombre_completo: '',
    numero_identificacion: '',
    fecha_nacimiento: '',
    genero: 'Masculino',
    telefono: '',
    email: '',
    ciudad: '',
    provincia: '',
    nacionalidad: 'Ecuador',
    peso_kg: '',
    numero_peleas_previas: 0,
    nombre_entrenador: '',
    gimnasio_id: '',
    gimnasio_no_registrado: false,
    nuevo_gimnasio_nombre: '',
    nuevo_gimnasio_entrenador: '',
    nuevo_gimnasio_ciudad: '',
    nuevo_gimnasio_telefono: ''
  });
  
  const [gymSearch, setGymSearch] = useState('');
  const [gymResults, setGymResults] = useState([]);
  const [selectedGym, setSelectedGym] = useState(null);
  const [comprobante, setComprobante] = useState(null);
  
  useEffect(() => {
    loadEventInfo();
  }, [slug]);
  
  const loadEventInfo = async () => {
    try {
      const { data } = await axios.get(`/public/events/${slug}`);
      setEventInfo(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar información del evento');
    } finally {
      setLoading(false);
    }
  };
  
  const searchGyms = async (query) => {
    if (query.length < 2) {
      setGymResults([]);
      return;
    }
    try {
      const { data } = await axios.get(`/public/gyms/search?q=${query}`);
      setGymResults(data);
    } catch (e) {
      console.error('Error searching gyms:', e);
    }
  };
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (gymSearch) searchGyms(gymSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [gymSearch]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre_completo || !formData.numero_identificacion || !formData.fecha_nacimiento) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }
    
    if (!formData.peso_kg || formData.peso_kg < 40 || formData.peso_kg > 150) {
      toast.error('Peso debe estar entre 40 y 150 kg');
      return;
    }
    
    if (!selectedGym && !formData.gimnasio_no_registrado) {
      toast.error('Selecciona un gimnasio o marca "Mi gimnasio no está registrado"');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Preparar payload
      const payload = {
        nombre_completo: formData.nombre_completo,
        numero_identificacion: formData.numero_identificacion,
        fecha_nacimiento: formData.fecha_nacimiento,
        genero: formData.genero,
        telefono: formData.telefono,
        email: formData.email,
        ciudad: formData.ciudad,
        provincia: formData.provincia || null,
        nacionalidad: formData.nacionalidad || 'Ecuador',
        peso_kg: parseFloat(formData.peso_kg),
        numero_peleas_previas: parseInt(formData.numero_peleas_previas) || 0,
        nombre_entrenador: formData.nombre_entrenador || null
      };
      
      if (selectedGym) {
        payload.gimnasio_id = selectedGym.id;
      } else if (formData.gimnasio_no_registrado) {
        payload.pending_gym_data = {
          nombre_gimnasio: formData.nuevo_gimnasio_nombre,
          nombre_entrenador: formData.nuevo_gimnasio_entrenador || null,
          ciudad: formData.nuevo_gimnasio_ciudad || null,
          telefono_contacto: formData.nuevo_gimnasio_telefono || null
        };
      }
      
      const { data } = await axios.post(`/public/events/${slug}/register`, payload);
      setRegistrationResult(data);
      
      // Subir comprobante si existe
      if (comprobante && data.registration_id) {
        const formDataFile = new FormData();
        formDataFile.append('file', comprobante);
        await axios.post(`/public/registrations/${data.registration_id}/upload-receipt`, formDataFile, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setSuccess(true);
      toast.success('¡Inscripción enviada exitosamente!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar inscripción');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl">Cargando...</div>
        </div>
      </div>
    );
  }
  
  if (!eventInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p>No se pudo cargar la información del evento.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (success && registrationResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-zinc-100 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-green-500">
            <CardHeader className="text-center">
              <div className="text-6xl mb-4">✅</div>
              <CardTitle className="text-2xl text-green-600">¡Inscripción Recibida!</CardTitle>
              <CardDescription>Tu inscripción ha sido registrada correctamente</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-zinc-50 p-4 rounded space-y-2 text-sm">
                <div><strong>Competencia:</strong> {eventInfo.nombre_competencia}</div>
                <div><strong>Fecha:</strong> {eventInfo.fecha_competencia}</div>
                <div><strong>Boxeador:</strong> {formData.nombre_completo}</div>
                <div><strong>Categoría:</strong> {registrationResult.categoria_edad} / {registrationResult.categoria_peso}</div>
                <div><strong>Estado:</strong> <span className={registrationResult.status === 'approved' ? 'text-green-600 font-semibold' : 'text-yellow-600 font-semibold'}>
                  {registrationResult.status === 'approved' ? 'Aprobado' : 'Pendiente de aprobación'}
                </span></div>
              </div>
              
              <div className="text-center text-sm text-zinc-600">
                {registrationResult.status === 'pending' && (
                  <p>El organizador revisará tu inscripción y te notificará.</p>
                )}
                {registrationResult.status === 'approved' && (
                  <p className="text-green-600 font-semibold">¡Ya estás inscrito en la competencia!</p>
                )}
              </div>
              
              <div className="text-center">
                <img src={homeLogoUrl} alt="Rank Box EC" className="h-24 w-auto mx-auto" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-zinc-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <img src={homeLogoUrl} alt="Rank Box EC" className="h-32 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-zinc-900 mb-2">Inscripción a Competencia</h1>
        </div>
        
        {/* Event Info */}
        <Card className="mb-6 border-2 border-yellow-400">
          <CardHeader>
            <CardTitle className="text-xl">{eventInfo.nombre_competencia}</CardTitle>
            <CardDescription>
              {eventInfo.fecha_competencia} • {eventInfo.ubicacion || eventInfo.ciudad}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><strong>Organizador:</strong> {eventInfo.organizador_nombre}</div>
              <div><strong>Modalidad:</strong> {eventInfo.modalidad || 'No especificada'}</div>
              {!eventInfo.is_registration_active && (
                <div className="col-span-2 text-red-600 font-semibold">
                  ⚠️ Las inscripciones están cerradas
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Registration Form */}
        {eventInfo.is_registration_active && (
          <form onSubmit={handleSubmit}>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Información Personal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nombre Completo *</label>
                    <Input
                      required
                      value={formData.nombre_completo}
                      onChange={(e) => setFormData({...formData, nombre_completo: e.target.value})}
                      placeholder="Ej: Juan Pérez García"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Cédula / Pasaporte *</label>
                    <Input
                      required
                      value={formData.numero_identificacion}
                      onChange={(e) => setFormData({...formData, numero_identificacion: e.target.value})}
                      placeholder="Ej: 1234567890"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Fecha de Nacimiento *</label>
                    <Input
                      required
                      type="date"
                      value={formData.fecha_nacimiento}
                      onChange={(e) => setFormData({...formData, fecha_nacimiento: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Género *</label>
                    <select
                      required
                      value={formData.genero}
                      onChange={(e) => setFormData({...formData, genero: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="Masculino">Masculino</option>
                      <option value="Femenino">Femenino</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Teléfono *</label>
                    <Input
                      required
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                      placeholder="Ej: 0999999999"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <Input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="Ej: correo@ejemplo.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Ciudad *</label>
                    <Input
                      required
                      value={formData.ciudad}
                      onChange={(e) => setFormData({...formData, ciudad: e.target.value})}
                      placeholder="Ej: Quito"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Provincia</label>
                    <Input
                      value={formData.provincia}
                      onChange={(e) => setFormData({...formData, provincia: e.target.value})}
                      placeholder="Ej: Pichincha"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Información Deportiva</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Peso (kg) *</label>
                    <Input
                      required
                      type="number"
                      step="0.1"
                      value={formData.peso_kg}
                      onChange={(e) => setFormData({...formData, peso_kg: e.target.value})}
                      placeholder="Ej: 75.5"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Número de Peleas Previas</label>
                    <Input
                      type="number"
                      value={formData.numero_peleas_previas}
                      onChange={(e) => setFormData({...formData, numero_peleas_previas: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Nombre del Entrenador</label>
                    <Input
                      value={formData.nombre_entrenador}
                      onChange={(e) => setFormData({...formData, nombre_entrenador: e.target.value})}
                      placeholder="Ej: Carlos López"
                    />
                  </div>
                </div>
                
                {/* Gym Selection */}
                <div>
                  <label className="block text-sm font-medium mb-1">Gimnasio que representa *</label>
                  
                  {!formData.gimnasio_no_registrado ? (
                    <>
                      <Input
                        value={gymSearch}
                        onChange={(e) => {
                          setGymSearch(e.target.value);
                          setSelectedGym(null);
                        }}
                        placeholder="Buscar gimnasio..."
                        disabled={!!selectedGym}
                      />
                      
                      {selectedGym && (
                        <div className="mt-2 p-3 bg-green-50 border border-green-300 rounded flex justify-between items-center">
                          <div>
                            <div className="font-medium">{selectedGym.nombre_gimnasio}</div>
                            <div className="text-sm text-zinc-600">{selectedGym.ciudad}</div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedGym(null);
                              setGymSearch('');
                            }}
                          >
                            Cambiar
                          </Button>
                        </div>
                      )}
                      
                      {gymResults.length > 0 && !selectedGym && (
                        <div className="mt-2 border rounded max-h-48 overflow-y-auto">
                          {gymResults.map((gym) => (
                            <div
                              key={gym.id}
                              className="p-3 hover:bg-zinc-50 cursor-pointer border-b last:border-b-0"
                              onClick={() => {
                                setSelectedGym(gym);
                                setGymSearch(gym.nombre_gimnasio);
                                setGymResults([]);
                              }}
                            >
                              <div className="font-medium">{gym.nombre_gimnasio}</div>
                              <div className="text-sm text-zinc-600">{gym.ciudad}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="mt-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.gimnasio_no_registrado}
                            onChange={(e) => setFormData({...formData, gimnasio_no_registrado: e.target.checked})}
                          />
                          Mi gimnasio no está registrado
                        </label>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-3 mt-3 p-4 bg-yellow-50 border border-yellow-300 rounded">
                        <p className="text-sm text-zinc-700">Completa la información de tu gimnasio:</p>
                        
                        <Input
                          required
                          value={formData.nuevo_gimnasio_nombre}
                          onChange={(e) => setFormData({...formData, nuevo_gimnasio_nombre: e.target.value})}
                          placeholder="Nombre del gimnasio *"
                        />
                        
                        <Input
                          value={formData.nuevo_gimnasio_entrenador}
                          onChange={(e) => setFormData({...formData, nuevo_gimnasio_entrenador: e.target.value})}
                          placeholder="Nombre del entrenador"
                        />
                        
                        <Input
                          value={formData.nuevo_gimnasio_ciudad}
                          onChange={(e) => setFormData({...formData, nuevo_gimnasio_ciudad: e.target.value})}
                          placeholder="Ciudad (ej: Quito, Guayaquil...)"
                        />
                        <p className="text-xs text-zinc-500 mt-1">
                          💡 Nota: Google Maps Autocomplete estará disponible cuando configures tu API key en el archivo .env
                        </p>
                        
                        <Input
                          value={formData.nuevo_gimnasio_telefono}
                          onChange={(e) => setFormData({...formData, nuevo_gimnasio_telefono: e.target.value})}
                          placeholder="Teléfono de contacto"
                        />
                        
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setFormData({...formData, gimnasio_no_registrado: false})}
                        >
                          Buscar gimnasio existente
                        </Button>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Payment Receipt Upload */}
                <div>
                  <label className="block text-sm font-medium mb-1">Comprobante de Pago</label>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setComprobante(e.target.files[0])}
                  />
                  <p className="text-xs text-zinc-500 mt-1">Sube una foto o PDF del comprobante de transferencia</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Submit */}
            <div className="text-center">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-yellow-500 text-black hover:bg-yellow-600 px-8 py-3 text-lg"
              >
                {submitting ? 'Enviando...' : '📋 Enviar Inscripción'}
              </Button>
              
              <p className="text-xs text-zinc-500 mt-3">
                Al enviar, confirmas que la información es correcta
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};


const Home = () => (
  <div className="bg-gradient-to-b from-zinc-950 to-zinc-900 text-white relative">
    <div className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-6 items-center">
      <div>
        <h1 className="text-4xl font-bold">Campeonatos de boxeo amateur</h1>
        <p className="mt-3 text-zinc-300">La plataforma que profesionaliza los campeonatos amateur del Ecuador.</p>
        <Link to="/rankings" className="inline-block mt-6"><Button data-testid="cta-rankings" className="bg-yellow-400 text-black hover:bg-yellow-300 px-6 py-6 rounded-full">Ver Rankings</Button></Link>
      </div>
      <div className="flex justify-center">
        <img src={homeLogoUrl} alt="Rank Box EC" className="h-40 w-auto md:h-56"/>
      </div>
    </div>

    {/* Powered by HIIT Club */}
    <div className="absolute right-4 bottom-4 flex items-center gap-2 text-xs text-zinc-300">
      <span>Powered by</span>
      <img src={poweredByLogoUrl} alt="HIIT Club" className="h-8 w-8 rounded-full border border-yellow-400 bg-black" />
    </div>
  </div>
);

function App() {
  const auth = useAuth();
  const logout = () => auth.setUser(null);
  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <BrowserRouter>
        <Header user={auth.user} onLogout={logout} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login onLogged={auth.setUser} />} />
          <Route path="/registro/:slug" element={<PublicRegistration />} />
          <Route path="/rankings" element={<Rankings />} />
          <Route path="/competitions" element={<Competitions />} />
          <Route path="/search" element={<Search />} />
          <Route path="/events/:id" element={<CompetitionDetail />} />
          <Route path="/fighters/:id/edit" element={<Protected user={auth.user} roles={["admin","gym_admin"]}><FighterEdit /></Protected>} />
          <Route path="/admin" element={<Protected user={auth.user} roles={["admin"]}><Admin /></Protected>} />
          <Route path="/affiliate" element={<Protected user={auth.user} roles={["gimnasio_afiliado","admin","gym_admin"]}><Affiliate user={auth.user} /></Protected>} />
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
